import AppContext from 'App/AppContext';
import { IPText } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { isCopilotPanelAvailable } from 'components/Copilot/copilotPanelAvailability';
import { EvidenceTaskContext, TaskAssistantAction } from 'components/Copilot/TaskAssistant/taskAssistant';
import { TaskAssistantPane } from 'components/Copilot/TaskAssistant/TaskAssistantPane';
import { DialogOk } from 'components/Dialogs/DialogOk';
import DialogYesNo from 'components/Dialogs/DialogYesNo';
import AddNewLink from 'components/Links/AddNewLink';
import OverlayLoader from 'components/Loading/OverlayLoader';
import { ICustomErrorActionType } from 'components/Notification/ErrorMessage';
import { LearnMore } from 'components/Notification/Info';
import { InquiryLineFilterKey } from 'components/ProjectInquiry/InquiryLineFilterKeys';
import { TaskFollowUpDialog } from 'components/Tasks/FollowUp/TaskFollowUpDialog';
import { globalKB_howToWorkWithForms } from 'globalConstants';
import { globalStackTokensGapSmall } from 'globalStyles';
import DateRange from 'models/dateRange';
import { CopilotContextProposalItem } from 'models/copilot/copilotContextProposal';
import {
  ControlSceneAIActionsPreviewFeatureToggleCode,
  isAuditCriteriaPreviewEnabled,
  isPreviewFeatureToggleEnabled,
} from 'models/copilot/copilotPreviewFeatureToggles';
import Entity, { EntityTypes } from 'models/entity';
import KPI, { KPITypes } from 'models/kpi/kpi';
import KPIData from 'models/kpi/kpiData';
import RecurringPattern from 'models/recurringPattern';
import ResourceLink from 'models/resourceLink';
import EvidenceLocation from 'models/evidenceLocation';
import ResourceList from 'models/resourceList';
import Tag from 'models/tag';
import Task, { TaskTypes } from 'models/tasks/task';
import { addTaskContextFrom } from 'models/tasks/taskContext';
import {
  applyTaskFollowUpSummary,
  areTaskRelationsDifferent,
  linkTaskRelationToTask,
  TaskRelation,
  TaskRelationship,
  toTaskRelationsTrace,
  toTaskRelationTrace,
} from 'models/tasks/taskRelation';
import { SystemTaskTypes, TaskKinds } from 'models/tasks/taskType';
import TaskTypeFormElement from 'models/tasks/taskTypeFormElement';
import { Fragment, lazy, Suspense, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getOutlookErrorMsg } from 'services/Api/apiErrors';
import { apiCreateLink, apiGetLinksForTask } from 'services/Api/linkService';
import { apiCreateTag } from 'services/Api/tagService';
import {
  apiAddTask,
  apiConvertTaskChecklistToForm,
  apiConvertTaskType,
  apiGetSingleTask,
  apiGetTaskInstances,
  apiGetTaskTypeForm,
  apiGetTaskRelations,
  apiGetTaskRescheduleRange,
  apiGetTaskTemplateIdByName,
  apiGetWebLinkForEvent,
  apiRemoveTask,
  apiRemoveTaskEvent,
  apiUpdateTask,
  apiUpdateTaskCompleted,
} from 'services/Api/taskService';
import { apiRequest } from 'services/Auth/authConfig';
import { FeatureTypes, hasUserFeature } from 'services/Auth/featurePermissions';
import logger from 'services/Logging/logService';
import { getFormVersionConflictCode } from 'components/Tasks/Forms/formVersionConflictText';
import { FormVersionConflictCode } from 'models/tasks/formVersion';
import { CommentTrailEntry } from 'models/comments';
import { apiAddComment } from 'services/Api/commentService';
import AppError, { normalizeError } from 'utils/appError';
import { ControllerExceptionCodes } from 'utils/controllerException';
import { addDateTimeDays, addDateTimeMinutes, getDateTimeDiffMinute } from 'utils/datetime';
import { newGuid } from 'utils/guid';
import { htmlToText } from 'utils/html';
import { getLocalStorageData, LocalStorageKeys } from 'utils/localstorage';
import { getLastUsedTaskAuthSchemaId, rememberTaskAuthSchemaId } from 'utils/taskSharingDefaults';
import { logTaskViewDiagnostic } from 'utils/taskViewDiagnostics';
import { navigateToExternalUrl } from 'utils/url';
import ConvertToAnnualPlanModal from './ConvertToAnnualPlanModal';
import { canUpdateTaskField, hasTaskDeletePermission, hasTaskUpdatePermission, TaskFieldTypes } from './TaskAuthHelper';
import EventTask from './TaskTypes/EventTask';
import EventTemplateTask from './TaskTypes/EventTemplateTask';
import MonitoringTask from './TaskTypes/MonitoringTask';
import NormalTask from './TaskTypes/NormalTask';
import { useTaskDetailView } from './TaskTypes/useTaskDetailView';
import ApprovalTask from './TaskTypes/SystemTaskTypes/ApprovalTask';
import ApprovalTemplateTask from './TaskTypes/SystemTaskTypes/ApprovalTemplateTask';
import AuditExecutionTask from './TaskTypes/SystemTaskTypes/AuditExecutionTask';
import AuditTemplateTask from './TaskTypes/SystemTaskTypes/AuditTemplateTask';
import ProcessingActivityApprovalTask from './TaskTypes/SystemTaskTypes/ProcessingActivityApprovalTask';
import TemplateTask from './TaskTypes/TemplateTask';
import NewTaskWizardWithAI from './TaskTypes/NewTask/NewTaskWizardWithAI';

interface ISingleTaskProps {
  isOpen: boolean; //Wherever possible, use stateVariable of type Task | undefined, and pass editTask!==undefined in isOpen
  year?: number; //When specified, updating a series task of type monitoring, the schedule (assignment and event) is also updated for instances in this audit year
  task: Task;
  disallowSave?: boolean; //when true, this component will not save the task to the database and only return the new/updated Task model
  disallowSchedule?: boolean;
  windowLevel?: number;
  navigateExternal?: boolean;
  formMode?: boolean;
  initialPivot?: string;
  useCreateWizard?: boolean; //a new normal task is created in the two-step wizard instead of the full dialog; set by the Add command of the Tasks scene only
  //a Kickstart inquiry window: the point it opens on (a link in Voortgang verantwoordelijken, Berichten lezen)
  inquiryOpensAt?: InquiryLineFilterKey;
  close: () => void;
  onSave: (task: Task, isNew: boolean, isCancelled: boolean) => void | Promise<void>; //Should only do the action of saving to state and not closing the modal as well
  onBeforeSave?: (oldTask: Task, newTask: Task) => JSX.Element | null; //Can cancel the save action by returning false. Not called on cancellation or on new tasks
  onRemove: (task: Task) => void; //Should only do the action of removing and not closing the modal as well
  onUpdate: (task: Task) => void; //Should update the task in state, called for changes to the task which are immediately saved (not waiting for user to click Save)
  //the names of the OTHER subtasks of the parent this task belongs to, as the parent holds them in
  //memory (unsaved ones included), for a task type that keeps its subtask names unique client-side
  siblingNames?: string[];
  //the criterion path that opened this evidence task: with it, the AI assistant pane opens beside the
  //task right away (when the AI assistant is available) and stays for the life of the modal. Every
  //other way of opening a task starts with the pane closed; the task's own AI button opens it.
  evidenceContext?: EvidenceTaskContext;
}

export interface ISingleTaskTypeProps {
  //a pane rendered as a third column at the right of the modal (the AI task assistant). A task type
  //that hosts it also hands onOpenAssistant to its top task bar, which then shows the AI button; the
  //types that do not (audit, approval and template tasks) never show the button.
  sidePane?: JSX.Element;
  //the action the assistant pane is open for; undefined when it is closed
  assistantAction?: TaskAssistantAction;
  //opens the pane for an action, or closes it when called with undefined
  onOpenAssistant?: (action: TaskAssistantAction | undefined) => void;
  onUpdateTask: (task: Task) => void; //called from the component per task type to update the task in the state here
  onUpdateOrgTask: (task: Task) => void; //called from the component per task type to update the task in the state here
  copyFromTemplate: (task: Task) => void;
  createFromTemplate: (task: Task) => void;
  onClickFollowUp: () => void;
  onLinkFollowUpToTemplate: (relation: TaskRelation, template: Task) => Promise<void>;
  onCreateEmptyFollowUpTask?: (relation: TaskRelation) => Promise<void>;
  onSave: (task: Task, schedule: boolean) => void;
  //saves the task and keeps this modal open, for a task type pane that must have the STORED task up to
  //date before it asks the server something about it. false means the save did not happen
  saveWithoutClose?: () => Promise<boolean>;
  //the open instances of this series master moved to the form's current version on the server: the
  //modal re-reads the master and hands it to the host the way it hands over a saved task
  onSeriesFormUpgraded?: () => Promise<void>;
  //a change reloads the instance tab
  instancesReloadToken?: number;
  onRemove: () => void;
  onCancel: () => void;
  onCreateNewTaskTemplate: () => void;
  setIsActionPending: (actionPending: boolean) => void;
  createInOutlook: () => Promise<void>;
  removeFromOutlook: () => Promise<void>;
  loadRescheduleRange: () => Promise<void>;
  addTagToTaskState: (tag: Tag) => void;
  removeTagFromTaskState: (tag: Tag) => void;
  addLinkToTaskState: (links: ResourceLink[], validateForm: boolean) => void;
  setLinkToTaskState: (links: ResourceLink[], validateForm: boolean) => void;
  removeLinkFromTaskState: (link: ResourceLink, validateForm: boolean) => void;
  onUpdateTaskForForm: (task: Task, validateForm: boolean) => void;
  navigateToEventURL: (task: Task) => void;
  onChangeCompletionDate: (completionDate: Date) => void;
  onUpdateKPIData: (data: KPIData) => void;
  onSetKPIData: (data: KPIData | KPIData[], instances?: Task[]) => void;
  onValidateForm: (errors: TaskTypeFormElement[]) => void;
  onBeforeSaveCallback: (callback: BeforeSaveCallback) => void;
  initData: (newTask: Task) => void;
  initPivot: () => void;
  reload: () => void;
  setCompactView: (newValue: boolean) => void;
  setFormMode: (newValue: boolean) => void;
  setSelectedPivot: (pivot: string) => void;
  //messages written in the Activity tab of a task that is not saved yet; posted after the first save
  pendingComments?: string[];
  onQueueComment?: (comment: string) => void;
  onRemovePendingComment?: (index: number) => void;
  onConvertChecklist?: () => void;
  onConvertTaskType?: (targetType: TaskTypes) => void;
  onError: (error: string | unknown) => void;
  onUploadEvidence: (evidenceList: ResourceList, location: EvidenceLocation) => void;
  onAfterErrorAction: (actionType: ICustomErrorActionType) => Promise<void>;
  error?: AppError;
  isOpen: boolean;
  task: Task;
  orgTask: Task;
  disallowSchedule?: boolean;
  disallowSave?: boolean;
  rescheduleDateRange: DateRange | undefined;
  tags: Tag[];
  links: ResourceLink[];
  isActionPending: boolean;
  isLoading: boolean;
  windowLevel?: number;
  showFormErrors: string | undefined;
  selectedPivot: string | undefined;
  hasFormFeature: boolean;
  hasPAFeature: boolean;
  navigateExternal?: boolean;
  formMode?: boolean;
  compactView: boolean | undefined;
  canUpdate: boolean;
  canDelete: boolean;
  masterTask?: Task | undefined;
  siblingNames?: string[];
}

export type BeforeSaveCallback = (task: Task) => Promise<Task | undefined>;

interface ISaveTaskOptions {
  includeTaskRelations?: boolean;
}

// The Kickstart inquiry windows load only when an inquiry task is opened: only inquiry tasks (their TaskKind) render them, and
// they bring the inquiry lists, their dialogs and the room rules with them.
const AssetInquiryTask = lazy(() => import('./TaskTypes/SystemTaskTypes/AssetInquiryTask'));
const OutcomeInquiryTask = lazy(() => import('./TaskTypes/SystemTaskTypes/OutcomeInquiryTask'));

const SingleTask = (props: ISingleTaskProps) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['translation', 'task', 'exceptions']);

  const [task, setTask] = useState<Task>(() => props.task.clone());
  const [orgTask, setOrgTask] = useState<Task>(() => props.task.clone());
  const [isInitializing, setIsInitializing] = useState<boolean>(false); //this is set when this module is initializing
  const [isActionPending, setIsActionPending] = useState<boolean>(false); //this is set for smaller async calls
  const [isLoading, setIsLoading] = useState<boolean>(false); //this is set for loading/removing causes the 'progress overlay'
  const [rescheduleDateRange, setRescheduleDateRange] = useState<DateRange | undefined>(undefined);
  const [taskTags, setTaskTags] = useState<Tag[]>([]);
  const [taskLinks, setTaskLinks] = useState<ResourceLink[]>([]);
  const [beforeSaveJSX, setBeforeSaveJSX] = useState<JSX.Element | undefined>(undefined);
  const [saveWithSchedule, setSaveWithSchedule] = useState<boolean>(false);
  const [followUpTask, setFollowUpTask] = useState<Task | undefined>(undefined);
  const [taskCopy, setTaskCopy] = useState<Task | undefined>(undefined);
  const [showFormErrors, setShowFormErrors] = useState<string | undefined>(undefined);
  const [pendingComments, setPendingComments] = useState<string[]>([]);
  const [saveInProgress, setSaveInProgress] = useState<boolean>(false);
  const [formErrors, setFormErrors] = useState<TaskTypeFormElement[]>([]);
  const [showFormErrorDialog, setShowFormErrorDialog] = useState<boolean>(false);
  const [selectedPivot, setSelectedPivot] = useState<string | undefined>(undefined); //this state is synced with the forms
  const [beforeSaveCallback, setBeforeSaveCallback] = useState<BeforeSaveCallback | undefined>(undefined);
  const [hasFormFeature] = useState<boolean>(() => hasUserFeature(appContext, FeatureTypes.TaskForm));
  const [hasPAFeature] = useState<boolean>(() => hasUserFeature(appContext, FeatureTypes.Automation));
  const [compactView, setCompactView] = useState<boolean | undefined>(undefined);
  //the AI assistant beside the task: open from the start on the criterion path (an evidence task),
  //otherwise opened by the task's AI button; undefined when closed
  const [assistantAction, setAssistantAction] = useState<TaskAssistantAction | undefined>(
    props.evidenceContext ? 'evidence' : undefined,
  );
  //raised after each "Upload evidence" that added files: the assistant runs "Check evidence" once per new value
  const [evidenceCheckRequest, setEvidenceCheckRequest] = useState<number>(0);
  //the open evidence dialog added links; read when it closes
  const evidenceDialogAddedLinks = useRef<boolean>(false);
  const [formMode, setFormMode] = useState<boolean | undefined>(props.formMode);
  const [showConvertChecklistDialog, setShowConvertChecklistDialog] = useState<boolean>(false);
  const [convertTaskTypeTarget, setConvertTaskTypeTarget] = useState<TaskTypes | undefined>(undefined);
  const [error, setError] = useState<AppError | undefined>(undefined);
  const [showUploadEvidenceDialog, setShowUploadEvidenceDialog] = useState<
    { list: ResourceList; location: EvidenceLocation } | undefined
  >(undefined);
  const [showFollowUpModal, setShowFollowUpModal] = useState<boolean>(false);
  const [showFollowUpSaveWarningDialog, setShowFollowUpSaveWarningDialog] = useState<boolean>(false);
  const [pendingLinkRelation, setPendingLinkRelation] = useState<TaskRelation | undefined>(undefined);
  const pendingLinkRelationRef = useRef<TaskRelation | undefined>(undefined);
  const [masterTask, setMasterTask] = useState<Task | undefined>(undefined);
  const taskDetailView = useTaskDetailView(task, () => initPivot());

  // Permissions
  const [canUpdate] = useState<boolean>(() => hasTaskUpdatePermission(props.task, appContext));
  const [canDelete] = useState<boolean>(() => hasTaskDeletePermission(props.task, appContext));
  const [instancesReloadToken, setInstancesReloadToken] = useState<number>(0);
  const [forceFullDialog, setForceFullDialog] = useState<boolean>(false); //the user left the create wizard through 'More options'

  //
  // Effects
  //
  useEffect(() => {
    if (props.isOpen) {
      initData(props.task);
      const calcCompactView = initCompactView();
      initPivot(calcCompactView);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.task, props.isOpen]);

  useEffect(() => {
    if (props.isOpen) {
      setForceFullDialog(false);
    }
  }, [props.isOpen]);

  useEffect(() => {
    if (appContext.isMobileView) {
      setCompactView(true);
    }
  }, [appContext.isMobileView]);

  useEffect(() => {
    if (compactView !== undefined) {
      if (!compactView) {
        initPivot(compactView);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compactView]);

  useEffect(() => {
    logTaskViewDiagnostic('tasks.singleTask.effectivePivot', {
      taskId: props.task.taskId,
      initialPivot: props.initialPivot,
      selectedPivot,
      compactView,
      formMode,
    });
  }, [compactView, formMode, props.initialPivot, props.task.taskId, selectedPivot]);

  //
  // Initialization
  //
  const initData = async (taskToInit: Task) => {
    //messages typed on a previous task must not be posted on this one
    setPendingComments([]);
    try {
      setIsInitializing(true);
      //load tags
      await appContext.globalDataCache.tags.getItems();
      setTaskTags(appContext.globalDataCache.tags.getItemsForId(taskToInit.tagIds));
      //load lists
      await appContext.globalDataCache.lists.getItems();
      //load subtasks
      const taskWithSubTasks = await loadSubTasks(taskToInit);
      // load task relations
      const taskWithTaskRelations = await loadTaskRelations(taskWithSubTasks);
      // load master task
      await loadMasterTask();
      //clone to state
      setOrgTask(taskWithTaskRelations.clone());
      setTask(taskWithTaskRelations.clone());
      //reset additional props
      setRescheduleDateRange(undefined);
      setFormErrors([]);
      setShowFormErrors(undefined);
      setIsLoading(false);
      setIsActionPending(false);
    } catch (err) {
      onError(err);
    } finally {
      setIsInitializing(false);
    }
  };

  const initCompactView = (): boolean => {
    let calcCompactView = false;

    //first, read from storage
    const compactViewFromStorage = getLocalStorageData(appContext, LocalStorageKeys.TaskCompactView);
    if (compactViewFromStorage) {
      calcCompactView = compactViewFromStorage === '1';
    }

    //disable for series
    if (props.task.isSeries()) {
      calcCompactView = false;
    }

    //disable for new tasks
    if (props.task.taskId === -1) {
      calcCompactView = false;
    }

    //enable in mobile view
    if (appContext.isMobileView) {
      calcCompactView = true;
    }

    setCompactView(calcCompactView);

    return calcCompactView;
  };

  const initPivot = (newCompactView?: boolean | undefined) => {
    if (props.initialPivot) {
      setSelectedPivot(props.initialPivot);

      return;
    }

    // The audit panes open on their own tab: they ask their questions on the FORM and have no
    // checklist tab at all, so a choice made here would either overrule them on their own layout or
    // land the user on a tab that does not exist - which renders as a blank pane.
    if (task.taskKind === TaskKinds.InternalAudit) {
      return;
    }

    // The new normal task view has the checklist or form as its main column and only Details, Context, ...
    // on the right: open the pivot the user used last, Details the first time
    const newViewPivot = task.taskType === TaskTypes.Normal ? taskDetailView.getNewViewPivot(task) : undefined;
    if (newViewPivot) {
      setSelectedPivot(newViewPivot);

      return;
    }

    newCompactView ??= compactView;
    if (hasFormFeature && task.taskTypeId && task.taskTypeId > 0) {
      setSelectedPivot('TaskType');
    } else if (task.checkList.items.length > 0) {
      setSelectedPivot('Checklist');
    } else if (newCompactView === true) {
      setSelectedPivot('TaskDetails');
      setFormMode(false);
    } else {
      setSelectedPivot('Checklist');
    }
  };

  const loadSubTasks = async (task: Task): Promise<Task> => {
    try {
      //load subtasks for tasktypes that can hold subtasks
      //subtasks are never returned by the back-end
      if (
        task.taskId !== -1 &&
        task.isSeries() &&
        (task.taskType === TaskTypes.Event || task.taskType === TaskTypes.EventTemplate)
      ) {
        //load for existing tasks
        const accessToken = await appContext.getAccessToken(apiRequest.scopes);
        const subTasks = await apiGetTaskInstances(task.taskId, accessToken, appContext.globalDataCache);
        task.instances = subTasks;
        //provide a new Guid for each subtasks for unique identification
        task.instances.forEach((instance) => (instance.id = newGuid()));
      } else if (
        task.taskId === -1 &&
        task.isSeries() &&
        (task.taskType === TaskTypes.Event || task.taskType === TaskTypes.EventTemplate) &&
        task.templateId
      ) {
        //load for new tasks
        //the task must have the template id set
        const accessToken = await appContext.getAccessToken(apiRequest.scopes);
        const subTasks = await apiGetTaskInstances(task.templateId, accessToken, appContext.globalDataCache);
        task.instances = subTasks.map((subTask) => {
          const newSubTask = new Task().applyTemplate(subTask, appContext);
          newSubTask.id = newGuid();
          newSubTask.taskMasterId = task.taskId;
          newSubTask.taskStateId = subTask.taskStateId;
          newSubTask.creator = appContext.user; //def creator is current user
          newSubTask.creatorId = appContext.user.id; //def creator is current user

          return newSubTask;
        });
      }

      //the subtasks of a just-linked procedure library (or one built via applyTemplate) exist only in the
      //browser, so they inherit the audit kind from their parent; the server stamps the kind on save
      if (task.taskKind === TaskKinds.InternalAudit) {
        task.instances?.forEach((instance) => (instance.taskKind = TaskKinds.InternalAudit));
      }

      return task;
    } catch (err) {
      onError(err);

      return task;
    }
  };

  const loadTaskRelations = async (task: Task): Promise<Task> => {
    if (task.taskId === -1) return task;

    try {
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const result = await apiGetTaskRelations(task.taskId, accessToken, appContext.globalDataCache);
      task.taskRelations = result ?? task.taskRelations;
      applyTaskFollowUpSummary(task);
    } catch (err) {
      onError(err);
    }

    return task;
  };

  const loadMasterTask = async () => {
    try {
      //
      // For monitoring instance tasks, load the master task to reflect information from the template such as:
      // - tags, links, context
      // For Approvals and ProcessingActivityApprovals, load the master to use the commenttrailId in the activity tab,
      // so that the comments between all approvers are shared

      // Skip loading for new tasks
      if (!props.task.taskMasterId || props.task.taskMasterId === -1 || props.task.taskId === -1) {
        return;
      }

      if (
        (props.task.taskType !== TaskTypes.Monitoring &&
          props.task.systemTaskType !== SystemTaskTypes.Approval &&
          props.task.systemTaskType !== SystemTaskTypes.ProcessingActivityApproval) ||
        props.task.isSeries()
      ) {
        return;
      }

      //set preferSeries is true because this way we get the master task even when the auth schema does not include the current user
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const tasks = await apiGetSingleTask(props.task.taskId, true, accessToken, appContext.globalDataCache);

      if (tasks.tasks && tasks.tasks.length > 0) {
        const masterTask = tasks.tasks[0];
        setMasterTask(masterTask);
      } else {
        appContext.showNotification(t('translation:General.Notifications.NotAvailable'), true);
        onCancel();
      }
    } catch (err) {
      onError(err);
    }
  };

  const reload = async (): Promise<void> => {
    try {
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const newTask = await apiGetSingleTask(task.taskId, false, accessToken, appContext.globalDataCache);
      if (newTask && newTask.tasks.length === 1) {
        initData(newTask.tasks[0]);
      } else {
        onCancel();
      }
    } catch (err) {
      const appError = AppError.fromApiError(err);
      appContext.showNotification(appError.message, true);
    }
  };

  //
  // Notification
  //
  const showNotification = (msg: string, isError: boolean = false, link?: { text: string; onClick: () => void }) => {
    appContext.showNotification(msg, isError, false, true, link);
  };

  //
  // Pending activity messages of a new task
  //
  const onQueueComment = (comment: string) => {
    setPendingComments((current) => [...current, comment]);
  };

  const onRemovePendingComment = (index: number) => {
    setPendingComments((current) => current.filter((_, i) => i !== index));
  };

  //posts the messages written before the task existed; a failing message must not undo the saved task
  const postPendingComments = async (commentTrailId: number) => {
    const comments = pendingComments;
    setPendingComments([]);

    if (comments.length === 0) {
      return;
    }

    // Without a comment trail none of the messages can be posted: report that instead of dropping them.
    let failedCount = commentTrailId ? 0 : comments.length;
    if (commentTrailId) {
      try {
        const accessToken = await appContext.getAccessToken(apiRequest.scopes);
        for (const text of comments) {
          try {
            const comment = new CommentTrailEntry();
            comment.commentTrailId = commentTrailId;
            comment.created = new Date();
            comment.comment = text;
            await apiAddComment(comment, accessToken);
          } catch {
            failedCount++;
          }
        }
      } catch {
        // no access token: none of the messages were posted
        failedCount = comments.length;
      }
    }

    if (failedCount > 0) {
      showNotification(t('task:PendingCommentsFailed', { failed: failedCount, total: comments.length }), true);
    }
  };

  //
  // Link functions
  //
  const createNewLinksAndSaveToTask = async (task: Task): Promise<Task> => {
    try {
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const newLinks = task.resourceLinkIds ? task.resourceLinkIds.slice(0) : [];

      //first create all new links and save the newly created Id into the array
      for (let i = 0; i < taskLinks.length; i++) {
        const rt = taskLinks[i];
        if (rt.linkId < 0) {
          const createdLink = await createLink(rt, accessToken);
          if (createdLink) {
            newLinks.push(createdLink.linkId);
          } else {
            throw new AppError('createNewLinksAndSaveToTask: Could not create link', 'couldNotCreateLink');
          }
        }
      }

      //save the modified link id's to the main task model
      task.resourceLinkIds = newLinks.filter((id) => id > 0);

      //Now do the same for new links created as attachments on KPI data
      task = await createNewLinksAndSaveToTaskKPIData(task);

      return task;
    } catch (err) {
      if (err instanceof AppError && err.code === 'couldNotCreateLink') {
        appContext.showNotification(t('library:LinkComponents.ErrorMessageAdd'), true);
      } else {
        onError(err);
      }

      return task;
    }
  };

  const createNewLinksAndSaveToTaskKPIData = async (task: Task): Promise<Task> => {
    try {
      if (!task.kpiData || task.kpiData.length === 0) return task;

      const accessToken = await appContext.getAccessToken(apiRequest.scopes);

      //create all new links and save the newly created Id into the array
      for (let i = 0; i < task.kpiData.length; i++) {
        const data = task.kpiData[i];
        const newLinks = data.contexts.filter((c) => c.entityType === EntityTypes.KPILink && c.entityId < 0);
        for (let j = 0; j < newLinks.length; j++) {
          const newLink = newLinks[j];
          const attachment = data.kpi?.attachments?.find((a) => a.linkId === newLink.entityId);
          if (!attachment || attachment.linkId > 0)
            throw new AppError('createNewLinksAndSaveToTaskKPIData: Attachment not found for link data context');
          const createdLink = await createLink(attachment, accessToken);
          if (createdLink) {
            newLink.entityId = createdLink.linkId;
            attachment.linkId = createdLink.linkId;
          } else {
            throw new AppError('createNewLinksAndSaveToTaskKPIData: Could not create link');
          }
        }
      }

      return task;
    } catch (err) {
      onError(err);

      return task;
    }
  };

  const addLinkToTaskState = async (items: ResourceLink[], validateForm: boolean) => {
    const newTaskLinks = taskLinks.slice(0);
    newTaskLinks.push(...items);
    setTaskLinks(newTaskLinks);
    const newTask = task.clone();
    newTask.resourceLinkIds = newTaskLinks.map((t) => t.linkId);
    setTask(newTask);
    if (showFormErrors && validateForm) {
      setShowFormErrors(newGuid());
    }
  };

  const setLinkToTaskState = async (items: ResourceLink[], validateForm: boolean) => {
    setTaskLinks(items);
    const newTask = task.clone();
    newTask.resourceLinkIds = items.map((t) => t.linkId);
    setTask(newTask);
    if (showFormErrors && validateForm) {
      setShowFormErrors(newGuid());
    }
  };

  const removeLinkFromTaskState = async (item: ResourceLink, validateForm: boolean) => {
    const newTaskLinks = taskLinks.slice(0).filter((_tag) => _tag.linkId !== item.linkId);
    setTaskLinks(newTaskLinks);
    const newTask = task.clone();
    newTask.resourceLinkIds = newTaskLinks.map((t) => t.linkId);
    setTask(newTask);
    if (showFormErrors && validateForm) {
      setShowFormErrors(newGuid());
    }
  };

  const createLink = async (item: ResourceLink, accessToken: string): Promise<ResourceLink | undefined> => {
    try {
      const newLink = await apiCreateLink(item, accessToken, appContext.globalDataCache);

      return newLink;
    } catch {
      return undefined;
    }
  };

  //
  // Tag functions
  //
  const createNewTagsAndSaveToTask = async (task: Task): Promise<Task> => {
    try {
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      let newTaskTags = [...taskTags];

      //first create all new tags and save the newly created Id into the array
      for (let i = 0; i < newTaskTags.length; i++) {
        const rt = newTaskTags[i];
        if (rt.tagId < 0) {
          const createdTag = await createTag(rt, accessToken);
          if (createdTag) {
            newTaskTags.push(createdTag);
          }
        }
      }

      //filter the temp tags with Id < 0
      newTaskTags = newTaskTags.filter((t) => t.tagId > 0);

      //save the modified tags to the main task model
      task.tagIds = newTaskTags.map((t) => t.tagId);

      return task;
    } catch (err) {
      onError(err);

      return task;
    }
  };

  const addTagToTaskState = async (item: Tag) => {
    const newTaskTags = taskTags.slice(0);
    newTaskTags.push(item);
    setTaskTags(newTaskTags);
    const newTask = task.clone();
    newTask.tagIds = newTaskTags.map((t) => t.tagId);
    setTask(newTask);
  };

  const removeTagFromTaskState = async (item: Tag) => {
    const newTaskTags = taskTags.slice(0).filter((_tag) => _tag.tagId !== item.tagId);
    setTaskTags(newTaskTags);
    const newTask = task.clone();
    newTask.tagIds = newTaskTags.map((t) => t.tagId);
    setTask(newTask);
  };

  const createTag = async (item: Tag, accessToken: string): Promise<Tag | undefined> => {
    try {
      const newTag = await apiCreateTag(item, accessToken);
      appContext.globalDataCache.tags.add(newTag);

      return newTag;
    } catch (err) {
      onError(err);

      return undefined;
    }
  };

  //
  // Templates
  //
  const onCreateNewTaskTemplate = async () => {
    try {
      setIsActionPending(true);

      //clone the current task
      let newTemplate = task.clone();
      //the answers travel as values only: a row copied with its id and contexts would stay attached to this task
      newTemplate.kpiData = task.kpiData?.map((data) => data.cloneAsNewAnswer());

      //set props
      newTemplate.taskMasterId = 0; //template is always a series
      newTemplate.taskId = 0; //to create a new task
      newTemplate.approved = undefined; //not approved
      newTemplate.completed = undefined; //not completed
      newTemplate.taskStateId = newTemplate.getFirstState(); //state is active
      newTemplate.eventId = undefined; //template cannot be scheduled
      newTemplate.hidden = false; //never hide a template
      newTemplate.recurringPattern = new RecurringPattern(); //reset to not active
      newTemplate.userId = undefined; //no user assignment
      newTemplate.user = undefined; //no user assignment
      newTemplate.owner = appContext.user; //def owner is current user
      newTemplate.ownerId = appContext.user.id; //def owner is current user
      newTemplate.creator = appContext.user; //def creator is current user
      newTemplate.creatorId = appContext.user.id; //def creator is current user
      newTemplate.templateId = undefined; //template itself is not a template
      newTemplate.webhookStatus = undefined; //no linked webhooks
      newTemplate.taskRelations = [];

      if (task.taskType === TaskTypes.Event || task.taskType === TaskTypes.EventTemplate) {
        newTemplate.taskType = TaskTypes.EventTemplate;
      } else {
        newTemplate.taskType = TaskTypes.Template;
      }

      //a template produces the current form: it is bound to the head, with the rows the head takes
      newTemplate = await bindCopyToFormHead(newTemplate);

      //create new tags and links
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const originalName = newTemplate.name;
      let duplicateIndex = 0;
      while (await apiGetTaskTemplateIdByName(newTemplate.name, accessToken)) {
        duplicateIndex += 1;
        newTemplate.name = `${originalName} (${duplicateIndex})`;
      }

      newTemplate = await createNewTagsAndSaveToTask(newTemplate);
      newTemplate = await createNewLinksAndSaveToTask(newTemplate);

      //add the task
      const templateTask = await apiAddTask(newTemplate, false, accessToken, appContext.globalDataCache);

      //add to global data cache
      appContext.globalDataCache.templates.add(templateTask);

      const templateUrl = `/admin/content/templates#taskId=${templateTask.taskId}`;
      showNotification(t('task:Template.SuccessToast', { name: templateTask.name }), false, {
        text: t('task:Template.OpenTemplate'),
        onClick: () => window.location.assign(templateUrl),
      });
    } catch (err) {
      showNotification(t('task:Template.ErrorToast', { err: (err as AppError).message }), true);
    } finally {
      setIsActionPending(false);
    }
  };

  //a follow-up of a saved task is shared exactly like that task, also when that is everyone (undefined);
  //only a follow-up of an unsaved task falls back to the sharing the user chose last (DevOps #826)
  const getFollowUpAuthSchemaId = (): number | undefined => {
    if (props.task.taskId !== -1) return props.task.authSchemaId;

    return getLastUsedTaskAuthSchemaId(appContext);
  };

  const createFromTemplate = async (template: Task) => {
    let output = new Task().applyTemplate(template, appContext);
    output.taskStates = appContext.globalDataCache.taskStates.items;
    output.taskStateId = output.getFirstState();
    output.taskRelations = [];
    output.creator = appContext.user; //def creator is current user
    output.creatorId = appContext.user.id; //def creator is current user

    // Fetch context before opening the modal so the follow-up task has the combined context
    // of the template and the current task. The nested SingleTask shows its own OverlayLoader during initData.
    if (props.task.taskId !== -1) {
      try {
        output = await addTaskContextFrom(output, props.task.taskId, appContext);
      } catch (err) {
        onError(err);

        return;
      }
    }

    openSecondTaskModal(output, true);
  };

  const createEmptyFollowUpTask = async () => {
    let output = new Task();
    output.taskType = TaskTypes.Normal;
    output.taskStates = appContext.globalDataCache.taskStates.items;
    output.taskStateId = output.getFirstState();
    output.taskRelations = [];
    output.name = t('translation:Task.Name.Default');
    output.owner = appContext.user.clone();
    output.ownerId = appContext.user.id;
    output.creator = appContext.user.clone();
    output.creatorId = appContext.user.id;
    output.authSchemaId = getFollowUpAuthSchemaId();

    if (props.task.taskId !== -1) {
      try {
        output = await addTaskContextFrom(output, props.task.taskId, appContext);
      } catch (err) {
        onError(err);

        return;
      }
    }

    openSecondTaskModal(output, true);
  };

  const copyFromTemplate = async (template: Task) => {
    const output = task.applyTemplate(template, appContext);
    output.creator = appContext.user; //def creator is current user
    output.creatorId = appContext.user.id; //def creator is current user
    output.owner = appContext.user;
    output.ownerId = appContext.user.id;

    const accessToken = await appContext.getAccessToken(apiRequest.scopes);

    //Load library items from the template
    const links = await apiGetLinksForTask(template.taskId, false, accessToken, appContext.globalDataCache);
    const newLinks: ResourceLink[] = [];

    for (const link of links) {
      newLinks.push(link);
    }

    setTaskLinks(newLinks);

    //set new tags from id's that were already copied from the template
    const newTaskTags = appContext.globalDataCache.tags.getItemsForId(output.tagIds);
    setTaskTags(newTaskTags);

    //update the task and switch to the new task type
    setTask(output);

    //switch to the form tab
    if (output.taskTypeId && output.taskTypeId > 0) {
      setSelectedPivot('TaskType');
    } else {
      setSelectedPivot('Checklist');
    }
  };

  const onConvertChecklist = async () => {
    try {
      setShowConvertChecklistDialog(false);

      //save the current task
      const savedTask = await onSave(task, false, true);
      if (!savedTask) return;

      setIsLoading(true);
      setIsActionPending(true);

      //convert on the back-end
      const trans_FormSuffix = t('task:CheckList.Conversion.FormSuffix');
      const trans_KPISuffix = t('task:CheckList.Conversion.KPISuffix');

      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const result = await apiConvertTaskChecklistToForm(
        savedTask.taskId,
        trans_FormSuffix,
        trans_KPISuffix,
        accessToken,
      );

      if (result?.taskType) {
        //add new task type to global cache
        if (!appContext.globalDataCache.taskTypes.has(result.taskType.taskTypeId)) {
          appContext.globalDataCache.taskTypes.add(result.taskType);
        }
        //update original task
        const newTask = savedTask.clone();
        newTask.taskTypeId = result.taskType.taskTypeId;
        setOrgTask(newTask);
        //update current task
        setTask(newTask.clone());
        //update parent
        await props.onSave(newTask, false, false);
      } else {
        appContext.showNotification(t('translation:General.Notifications.NotAvailable'), true);
      }
    } catch (err) {
      onError(err);
    } finally {
      setIsLoading(false);
      setIsActionPending(false);
    }
  };

  const onConvertTaskType = async (targetType: TaskTypes, recurringPattern?: RecurringPattern) => {
    try {
      setConvertTaskTypeTarget(undefined);

      //save the current task first so the server converts the latest version
      const savedTask = await onSave(task, false, true);
      if (!savedTask) return;

      //only the loading flag: the event detail renders its subtask list in loading mode while an action
      //is pending, and that list cannot render its placeholder rows for an event without subtasks
      setIsLoading(true);

      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const convertedTask = await apiConvertTaskType(
        savedTask.taskId,
        targetType,
        accessToken,
        appContext.globalDataCache,
        recurringPattern,
      );

      if (convertedTask) {
        //the type switch below re-renders the detail for the new type from this task
        setOrgTask(convertedTask);
        setTask(convertedTask.clone());
        //the selected tab belonged to the old type's layout (the normal task view selects 'TaskDetails', which
        //the annual plan task only has in compact view): select a tab the converted task shows
        setSelectedPivot(
          hasFormFeature && convertedTask.taskTypeId && convertedTask.taskTypeId > 0 ? 'TaskType' : 'Checklist',
        );
        await props.onSave(convertedTask, false, false);

        if (targetType === TaskTypes.Monitoring) {
          appContext.showNotification(t('task:ConvertToAnnualPlanModal.Converted'), false, false, true);
        }
      } else {
        appContext.showNotification(t('translation:General.Notifications.NotAvailable'), true, false, true);
      }
    } catch (err) {
      onError(err);
    } finally {
      setIsLoading(false);
    }
  };

  //
  // Task functions
  //
  const loadRescheduleRange = async () => {
    try {
      setIsActionPending(true);
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const range = await apiGetTaskRescheduleRange(task.taskId, accessToken);
      setRescheduleDateRange(range);
    } catch (err) {
      onError(err);
    } finally {
      setIsActionPending(false);
    }
  };

  const onValidateForm = (errors: TaskTypeFormElement[]) => {
    setFormErrors(errors);

    //when there are no errors, call save again
    if (errors.length === 0 && saveInProgress) {
      onSave(task, saveWithSchedule);
    } else {
      if (task.isCompleted()) {
        //revert state
        const newTask = task.clone();
        newTask.taskStateId = props.task.taskStateId;
        newTask.completed = undefined;
        if (newTask.isCompleted()) {
          newTask.taskStateId = props.task.getFirstState();
        }
        setTask(newTask);
      }
      if (saveInProgress) {
        //show message to user that the form has errors and show the form
        setShowFormErrorDialog(true);
        setSelectedPivot('TaskType');
      }
    }

    setSaveInProgress(false);
  };

  const onSave = async (
    task: Task,
    schedule: boolean,
    dontClose: boolean = false,
    options?: ISaveTaskOptions,
  ): Promise<Task | undefined> => {
    try {
      setIsLoading(true);

      //call the onBeforeSave callback
      //specialized task handlers can modify the task before save
      //cancel on returning undefined
      if (beforeSaveCallback) {
        const newTask = await beforeSaveCallback(task);
        if (newTask) {
          task = newTask;
        } else {
          return undefined;
        }
      }

      //when this task has a form, validate it
      if (task.isCompleted() && hasFormFeature && task.taskTypeId && (!showFormErrors || formErrors.length > 0)) {
        setSaveInProgress(true);
        setSaveWithSchedule(schedule);
        setShowFormErrors(newGuid()); //when this changes, onValidateForm callback is executed

        return undefined;
      }

      //first create any new tags and update the id's of the tags accordingly in the task
      let taskToSave = await createNewTagsAndSaveToTask(task);

      //then, create any new links and update the id's of the links accordingly in the task
      taskToSave = await createNewLinksAndSaveToTask(taskToSave);
      const includeTaskRelations =
        options?.includeTaskRelations ?? areTaskRelationsDifferent(taskToSave.taskRelations, orgTask.taskRelations);

      if (!props.disallowSave) {
        if (taskToSave.taskId === -1) {
          const accessToken = await appContext.getAccessToken(apiRequest.scopes);
          const newTask = await apiAddTask(taskToSave, schedule, accessToken, appContext.globalDataCache, props.year, {
            includeTaskRelations,
            originalTaskRelations: orgTask.taskRelations,
          });

          await postPendingComments(newTask.commentTrailId);
          //the sharing choice of a new task is the default for the next one
          if (taskToSave.taskType === TaskTypes.Normal || taskToSave.taskType === TaskTypes.Event) {
            rememberTaskAuthSchemaId(appContext, taskToSave.authSchemaId);
          }

          await props.onSave(newTask, true, false);
          taskToSave = newTask;
        } else {
          if (props.onBeforeSave && !beforeSaveJSX) {
            const element = props.onBeforeSave(props.task, taskToSave);
            if (element) {
              //setting this will trigger the dialog to ask the user additional stuff
              //the dialog will call onSave again after confirmation or cancel the save operation
              setSaveWithSchedule(schedule);
              setBeforeSaveJSX(element);

              return undefined;
            }
          } else {
            setBeforeSaveJSX(undefined);
            setSaveWithSchedule(false);
          }

          const accessToken = await appContext.getAccessToken(apiRequest.scopes);
          const updatedTask = await apiUpdateTask(
            taskToSave,
            schedule,
            accessToken,
            appContext.globalDataCache,
            props.year,
            { includeTaskRelations, originalTaskRelations: orgTask.taskRelations },
          );

          if (updatedTask) {
            await props.onSave(updatedTask, false, false);
            taskToSave = updatedTask;
          }

          const updatedControlIds = updatedTask?.controlIds;
          if (
            task.taskType === TaskTypes.Monitoring &&
            updatedControlIds &&
            !task.controlIds.every((controlId) => updatedControlIds.includes(controlId))
          ) {
            appContext.showNotification(t('task:Context.MonitoringDisabledNotAdded'), false, true);
          }
        }

        if (schedule) {
          showNotification(t('task:Outlook.OutlookScheduledToast'));
        }
      } else {
        //do not save to the back-end
        if (includeTaskRelations) {
          applyTaskFollowUpSummary(taskToSave);
        }
        await props.onSave(taskToSave, taskToSave.taskId === -1, false);
      }

      if (!dontClose) {
        props.close();
      }

      return taskToSave;
    } catch (err) {
      let toastShown: boolean = false;
      const formConflictCode = getFormVersionConflictCode(err);
      if (formConflictCode === FormVersionConflictCode.TaskFormVersionMoved) {
        await recoverFromFormVersionMove(task);
        toastShown = true;
      } else if (AppError.isControllerException(err)) {
        if (AppError.getControllerExceptionCode(err) === ControllerExceptionCodes.Code_General_NotAuthorized) {
          appContext.showNotification(t('exceptions:Task.NoAuth'), true);
          toastShown = true;
        } else if (
          AppError.getControllerExceptionCode(err) === ControllerExceptionCodes.Code_Task_CalendarUnavailable
        ) {
          // the API saved the task before the Outlook calendar call went unanswered: warn, then show the saved state
          appContext.showNotification(AppError.getControllerExceptionMsg(err, t) ?? '', false, true, true);
          toastShown = true;
          if (task.taskId !== -1) {
            await reload();
          } else if (!dontClose) {
            // the new task exists on the server; a second Save would create it twice
            props.close();
          }
        } else {
          appContext.showNotification(AppError.getControllerExceptionMsg(err, t), true, false, true);
          toastShown = true;
        }
      } else {
        const outlookError = getOutlookErrorMsg(err as AppError, t);
        if (outlookError) {
          showNotification(t('task:Outlook.OutlookFailureToast', { error: outlookError }), true);
          toastShown = true;
        }
      }
      if (!toastShown) {
        onError(err);
      }

      return task;
    } finally {
      setIsLoading(false);
    }
  };

  const onRemove = async () => {
    try {
      if (task.taskId !== -1) {
        setIsLoading(true);
        const accessToken = await appContext.getAccessToken(apiRequest.scopes);
        await apiRemoveTask(task, accessToken);
        props.onRemove(task);

        //set the original task, otherwise, when the same task is opened again, the cancelled changes are stil visible
        setTask(props.task);
        setTaskTags([]);
        setTaskLinks([]);
        setRescheduleDateRange(undefined);

        props.close();
      }
    } catch (err) {
      if (AppError.isControllerException(err)) {
        appContext.showNotification(AppError.getControllerExceptionMsg(err, t), true, false, true);
      } else {
        onError(err);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const onCancel = async () => {
    //the host gets the saved state the modal holds: the same as the task it opened, unless the server
    //moved the task to another form version meanwhile (an upgrade from the form tab, a fork), which
    //onUpdateTaskForForm recorded on orgTask. Handing back the opened task would reopen the old pin
    await props.onSave(orgTask, task.taskId === -1, true);

    //set the original task, otherwise, when the same task is opened again, the cancelled changes are still visible
    setTask(orgTask.clone());
    setPendingComments([]);
    setTaskTags([]);
    setTaskLinks([]);
    setRescheduleDateRange(undefined);

    props.close();
  };

  const createInOutlook = async (): Promise<void> => {
    try {
      await onSave(task, true);
    } catch (err) {
      onError(err);
    }
  };

  const removeFromOutlook = async (): Promise<void> => {
    try {
      setIsLoading(true);
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      if (task.taskType === TaskTypes.Event && task.isSeries() && task.instances) {
        //remove all schedules from subtasks
        for (let idx = 0; idx < task.instances.length; idx++) {
          const instance = task.instances[idx];
          await apiRemoveTaskEvent(instance, accessToken);
          instance.eventId = undefined;
        }

        setTask(task.clone());
      } else {
        //remove schedule from current task
        await apiRemoveTaskEvent(task, accessToken);

        const newTask = task.clone();
        newTask.eventId = undefined;
        setTask(newTask);

        const newOrgTask = orgTask.clone();
        newOrgTask.eventId = undefined;
        setOrgTask(newOrgTask);

        props.onUpdate(newTask);
      }
    } catch (err) {
      let toastShown: boolean = false;
      if (AppError.getControllerExceptionCode(err) === ControllerExceptionCodes.Code_General_NotAuthorized) {
        appContext.showNotification(t('exceptions:Task.NoAuth'), true);
        toastShown = true;
      }
      if (!toastShown) onError(err);
    } finally {
      setIsLoading(false);
    }
  };

  //Reopening a finished task saves right away and deliberately keeps the modal open: correcting the
  //work is the point of reopening, so the saved task is pushed back into state and to the list
  //through onUpdate - never through onSave, which is the channel that closes the modal.
  const reopenTask = async () => {
    try {
      setIsLoading(true);
      const newTask = task.clone();
      newTask.setReopened();
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const updatedTask = await apiUpdateTask(newTask, false, accessToken, appContext.globalDataCache, props.year);
      const savedTask = updatedTask ?? newTask;
      setTask(savedTask.clone());
      setOrgTask(savedTask.clone());
      props.onUpdate(savedTask);
    } catch (err) {
      onError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const updateCompletionDate = async (completionDate: Date) => {
    try {
      setIsLoading(true);
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      //clone the original task with the start date that was not changed yet
      //the back-end checks whether the new completed date is not before the original start date
      const updatedOrgTask = orgTask.clone();
      updatedOrgTask.completed = completionDate;
      await apiUpdateTaskCompleted(updatedOrgTask, accessToken);
      setOrgTask(updatedOrgTask);
      //also update the current (changed) task
      const updatedTask = task.clone();
      updatedTask.completed = completionDate;
      setTask(updatedTask);
      props.onUpdate(updatedTask);
    } catch (err) {
      onError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const onUpdate = async (updatedTask: Task): Promise<void> => {
    if (updatedTask.taskId !== -1 && updatedTask.taskId !== task.taskId) {
      //task has changed: load tags
      setTaskTags(appContext.globalDataCache.tags.getItemsForId(updatedTask.tagIds));
    }
    setTask(updatedTask);
  };

  const onSaveAsNewTask = async () => {
    const newBaseTask = task.clone();
    newBaseTask.taskId = -1; //new tasks have task Id set to -1
    newBaseTask.eventId = undefined; //new tasks are not created in Outlook yet
    newBaseTask.taskRelations = [];
    //the answers travel as values only: a row copied with its id and contexts would stay attached to this task
    newBaseTask.kpiData = task.kpiData?.map((data) => data.cloneAsNewAnswer());

    if (newBaseTask.taskType === TaskTypes.Normal) {
      //new task must be loose from its parent: in other words: cannot create an instance of the same parent
      //this is because normal tasks that are an instance or part of a series that have a distinct pattern. Any exceptions would break that
      newBaseTask.taskMasterId = undefined;
    }

    newBaseTask.name = t('task:CopyOf') + newBaseTask.name;

    //the new startdate is the current date
    //apply the start time from the task being copied
    //recalculate end-time and deadline based on defaults
    const sd = newBaseTask.startDateTime;
    const cd = new Date();
    const duration = getDateTimeDiffMinute(newBaseTask.endDateTime, sd);
    newBaseTask.startDateTime = new Date(
      cd.getFullYear(),
      cd.getMonth(),
      cd.getDate(),
      sd.getHours(),
      sd.getMinutes(),
      0,
    );
    newBaseTask.endDateTime = addDateTimeMinutes(newBaseTask.startDateTime, duration);
    newBaseTask.deadline = addDateTimeDays(newBaseTask.endDateTime, 14);
    if (newBaseTask.recurringPattern) {
      newBaseTask.recurringPattern.startDate = newBaseTask.startDateTime;
    }

    //set state to 'first' state when the task being copied was completed
    newBaseTask.completed = undefined;
    if (newBaseTask.taskStateId === newBaseTask.getCompletedState()) {
      newBaseTask.taskStateId = newBaseTask.getFirstState();
    }

    try {
      //copy document links. Load them from the task being copied
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const links = await apiGetLinksForTask(task.taskId, false, accessToken, appContext.globalDataCache);
      setTaskLinks(links);

      //update the task in state
      openSecondTaskModal(await bindCopyToFormHead(newBaseTask), false);
    } catch (err) {
      //a failed read of the links or of the form's current version must not leave the user without
      //a modal and without a message
      appContext.setError(err);
    }
  };

  //A copy of this task (save as new task, save as template) is created on the CURRENT version of its
  //form, while this task may be pinned to an older one: the server binds a new task to the head. Filled
  //on the old version, the copy would post answers to questions the head no longer asks (rows no form
  //shows) and skip the questions only the head asks. So the copy opens on the head, with the rows the
  //head still takes, judged the way the version-move recovery judges them
  const bindCopyToFormHead = async (copy: Task): Promise<Task> => {
    if (!copy.taskTypeId) return copy;

    const head = appContext.globalDataCache.taskTypes.getHead(copy.taskTypeId, copy.formVersion?.taskTypeFamilyId);
    if (!head || head.taskTypeId === copy.taskTypeId) return copy;

    const accessToken = await appContext.getAccessToken(apiRequest.scopes);
    const fields = await apiGetTaskTypeForm(head.taskTypeId, accessToken);
    const fieldByKpiId = new Map(
      fields.filter((field) => field.kpiId !== undefined).map((field) => [field.kpiId, field]),
    );
    //the rows travel as values only (cloneAsNewAnswer strips the definition), so the type the value
    //was recorded under comes from the version this task is on, not from the row
    const oldTypeByKpiId = new Map(
      (copy.formVersion?.form ?? [])
        .filter((field) => field.kpiId !== undefined && field.kpi !== undefined)
        .map((field) => [field.kpiId, field.kpi?.type]),
    );
    const rows = copy.kpiData ?? [];
    copy.assignForm(head);
    const version = head.clone();
    version.form = fields;
    copy.formVersion = version;
    copy.kpiData = rows.flatMap((row) => {
      const field = fieldByKpiId.get(row.kpiId);
      if (field === undefined) return [];
      if (!field.kpi || valueFitsDefinition(row, field.kpi, oldTypeByKpiId.get(row.kpiId))) return [row];
      //a value the head's definition no longer takes goes; the comment is the user's own words and stays
      if (!row.resultComment) return [];
      const kept = row.clone();
      kept.resultNumber = undefined;
      kept.resultText = undefined;
      kept.resultDate = undefined;

      return [kept];
    });

    return copy;
  };

  const navigateToEventURL = async (task: Task) => {
    try {
      setIsActionPending(true);

      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const webUrl = await apiGetWebLinkForEvent(task, accessToken);

      if (webUrl) {
        navigateToExternalUrl(webUrl, undefined, '');
      } else {
        showNotification(t('task:Outlook.OpenInOutlookError'));
      }
    } catch (err) {
      onError(err);
    } finally {
      setIsActionPending(false);
    }
  };

  const getDisallowSchedule = (): boolean => {
    return props.disallowSchedule || (task.taskType === TaskTypes.Monitoring && task.isSeries() && !props.year);
  };

  //a form can only contain unique KPI's so we can find on criterium kpiId only
  const mergeKPIData = (kpiData: KPIData[] | undefined, rows: KPIData[]): KPIData[] => {
    const merged = kpiData ?? [];
    for (const row of rows) {
      const dataIdx = merged.findIndex((k) => k.kpiId === row.kpiId);
      if (dataIdx >= 0) {
        merged[dataIdx] = row.clone();
      } else {
        merged.push(row.clone());
      }
    }

    return merged;
  };

  //functional updates: the form reports several rows in one render pass (defaults, expressions);
  //each update builds on the previous one instead of on the task this render started with
  const onUpdateKPIData = (data: KPIData) => {
    setTask((current) => {
      const newTask = current.clone();
      newTask.kpiData = mergeKPIData(newTask.kpiData, [data]);

      return newTask;
    });

    if (showFormErrors) {
      setShowFormErrors(newGuid());
    }
  };

  const onSetKPIData = (data: KPIData | KPIData[], instances?: Task[]) => {
    //set the KPI data that is loaded for the form
    const rows = Array.isArray(data) ? data : [data];

    setTask((current) => {
      const newTask = current.clone();
      newTask.kpiData = mergeKPIData(newTask.kpiData, rows);

      //update instances
      if (instances && instances.length > 0) {
        if (!newTask.instances) newTask.instances = [];
        for (const newInstance of instances) {
          const currentInstanceIdx = newTask.instances.findIndex((t) => t.id === newInstance.id);
          if (currentInstanceIdx >= 0) {
            newTask.instances[currentInstanceIdx] = newInstance;
          }
        }
      }

      return newTask;
    });

    //set the KPI data also on the original task so that these are the same
    setOrgTask((current) => {
      const newOrgTask = current.clone();
      newOrgTask.kpiData = mergeKPIData(newOrgTask.kpiData, rows);

      return newOrgTask;
    });
  };

  const onUpdateTaskForForm = (task: Task, validateForm: boolean) => {
    setTask(task);
    //the only route by which a form change reaches this handler is a fork: editing a form that is in
    //use creates a new version and the server repoints this task to it. That move is not an edit of
    //the user's, so the saved state follows it - otherwise the task comes up unsaved on a field nobody
    //touched. Picking another form is a different route (onUpdateTask) and stays a change like any other
    if (task.taskTypeId !== orgTask.taskTypeId) {
      const newOrgTask = orgTask.clone();
      newOrgTask.taskTypeId = task.taskTypeId;
      newOrgTask.formVersion = task.formVersion?.clone();
      //the rows follow the move as well: an upgrade hands the task over without them, so that the
      //reload takes the rows the server re-seeded, and the saved state must not keep the old ones
      newOrgTask.kpiData = task.kpiData?.map((data) => data.clone());
      setOrgTask(newOrgTask);
    }
    if (showFormErrors && validateForm) {
      setShowFormErrors(newGuid());
    }
  };

  const setPendingFollowUpLinkRelation = (relation: TaskRelation | undefined) => {
    pendingLinkRelationRef.current = relation;
    setPendingLinkRelation(relation);
  };

  const saveFollowUpRelationUpdate = async (updatedTask: Task): Promise<Task> => {
    const taskToSave = updatedTask.clone();
    applyTaskFollowUpSummary(taskToSave);

    if (!props.disallowSave) {
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const savedParentTask = await apiUpdateTask(
        taskToSave,
        false,
        accessToken,
        appContext.globalDataCache,
        props.year,
        { includeTaskRelations: true, originalTaskRelations: orgTask.taskRelations },
      );
      const taskToReturn = savedParentTask ?? taskToSave;
      logger.debug('FollowUpLink: parent saved', {
        parentTaskId: taskToReturn.taskId,
        hasFollowUp: taskToReturn.hasFollowUp,
        hasUnlinkedFollowUp: taskToReturn.hasUnlinkedFollowUp,
        followUpTotal: taskToReturn.followUpTotal,
        relations: toTaskRelationsTrace(taskToReturn.taskRelations),
      });
      await props.onSave(taskToReturn, false, false);

      return taskToReturn;
    }

    await props.onSave(taskToSave, false, false);

    return taskToSave;
  };

  const onSaveSecondTaskModal = async (savedTask: Task, isNew?: boolean, isCanceled?: boolean) => {
    if (taskCopy || task.taskId === -1 || isCanceled) {
      if (isCanceled) {
        setPendingFollowUpLinkRelation(undefined);
      }

      closeSecondTaskModal();

      return;
    }

    try {
      setIsLoading(true);
      let updatedTask = task.clone();
      const relationToLink = pendingLinkRelationRef.current ?? pendingLinkRelation;
      logger.debug('FollowUpLink: child modal save received', {
        parentTaskId: task.taskId,
        savedTaskId: savedTask.taskId,
        savedTaskName: savedTask.name,
        isNew,
        isCanceled,
        pendingRelation: toTaskRelationTrace(relationToLink),
        relationsBefore: toTaskRelationsTrace(updatedTask.taskRelations),
      });

      if (relationToLink) {
        updatedTask.taskRelations = linkTaskRelationToTask(updatedTask.taskRelations, relationToLink, savedTask);
      } else {
        const newTaskRelation = new TaskRelation(props.task.taskId, savedTask.taskId, TaskRelationship.FollowUp);
        newTaskRelation.taskTo = savedTask;
        updatedTask.taskRelations = [...updatedTask.taskRelations, newTaskRelation];
      }

      logger.debug('FollowUpLink: parent relations prepared for save', {
        parentTaskId: updatedTask.taskId,
        relationsAfter: toTaskRelationsTrace(updatedTask.taskRelations),
      });
      applyTaskFollowUpSummary(updatedTask);
      updatedTask = await saveFollowUpRelationUpdate(updatedTask);
      setTask(updatedTask);
      setOrgTask(updatedTask.clone());
      setPendingFollowUpLinkRelation(undefined);

      closeSecondTaskModal();
    } catch (err) {
      logger.debug('FollowUpLink: auto-link failed', err);
      onError(err);
    } finally {
      setIsLoading(false);
    }
  };

  // There is currently two ways of opening a second task modal:
  // - Follow-up task
  // - Copy task
  // Both require different behavior when saving the task or closing the modal.
  // So we need to keep track of which type of modal is open.
  const openSecondTaskModal = (task: Task, followUp: boolean) => {
    if (followUp) {
      setFollowUpTask(task);
    } else {
      setTaskCopy(task);
    }
  };

  // Only one additional task modal can be open at a time, so we can safely set both to undefined.
  const closeSecondTaskModal = () => {
    setFollowUpTask(undefined);
    setTaskCopy(undefined);
  };

  const onUploadEvidence = (evidenceList: ResourceList, location: EvidenceLocation) => {
    setShowUploadEvidenceDialog({ list: evidenceList, location });
  };

  //
  // Render helpers
  //
  const getUploadEvidenceDialog = (): JSX.Element | null => {
    if (showUploadEvidenceDialog && task.isWorkableTask()) {
      return (
        <AddNewLink
          hideButton={true}
          selectedList={showUploadEvidenceDialog.list}
          uploadFolderId={showUploadEvidenceDialog.location.folderDriveItemId}
          uploadFolderName={showUploadEvidenceDialog.location.folderName}
          existingLinks={taskLinks}
          addLinks={(links) => {
            evidenceDialogAddedLinks.current = links.length > 0;
            void addLinkToTaskState(links, false);
          }}
          allowCustomLists={true}
          allowDocumentLibraries={true}
          allowSitePages={true}
          allowWebURLs={true}
          close={() => {
            setShowUploadEvidenceDialog(undefined);
            setSelectedPivot('Resources');
            //the files of the batch are judged once, after the last one: the assistant saves the task first
            if (evidenceDialogAddedLinks.current && isEvidenceCheckAvailable()) {
              openAssistant('check');
              setEvidenceCheckRequest((value) => value + 1);
            }
            evidenceDialogAddedLinks.current = false;
          }}
          disabled={false}
          hideWholeList={true}
          entityContext={true}
        />
      );
    }

    return null;
  };

  const getAdditionalTaskModal = (): JSX.Element | null => {
    // Only one or neither should be set at all times.
    const task = followUpTask ?? taskCopy;
    if (!task) return null;

    return (
      <SingleTask
        isOpen={task !== undefined}
        close={closeSecondTaskModal}
        task={task}
        onSave={onSaveSecondTaskModal}
        onRemove={closeSecondTaskModal}
        onUpdate={closeSecondTaskModal}
        year={undefined}
        disallowSchedule={false}
        windowLevel={(props.windowLevel ?? 0) + 1}
        navigateExternal={props.navigateExternal}
      />
    );
  };

  const openFollowUpFlow = (taskToCheck: Task = task): boolean => {
    // Guard: only 1 unlinked follow-up allowed per task (DB unique constraint).
    // Use hasUnlinkedFollowUp flag since taskRelations may not be loaded yet
    // (they are fetched separately by the Context tab, not included in the main task DTO).
    if (taskToCheck.hasUnlinkedFollowUp) {
      showNotification(t('task:FollowUp.AlreadyPending'));
      setSelectedPivot('Context');

      return false;
    }

    setShowFollowUpModal(true);

    return true;
  };

  const onClickFollowUp = () => {
    if (task.hasUnlinkedFollowUp) {
      openFollowUpFlow();

      return;
    }

    if (!orgTask.isEqual(task)) {
      setShowFollowUpSaveWarningDialog(true);

      return;
    }

    openFollowUpFlow();
  };

  //Save without closing, for a task type pane that must have the stored task up to date before it asks
  //the server something about it. onSave hands back the task it was given when it refused the save (a
  //form validation stop) or when it failed, so identity - not undefined - is what tells the two apart.
  const saveWithoutClose = async (): Promise<boolean> => {
    const savedTask = await onSave(task, false, true);
    if (!savedTask || savedTask === task) return false;

    setTask(savedTask.clone());
    setOrgTask(savedTask.clone());

    return true;
  };

  const onSaveBeforeFollowUp = async () => {
    setShowFollowUpSaveWarningDialog(false);
    const savedTask = await onSave(task, false, true);

    if (!savedTask || savedTask === task) return;

    setTask(savedTask.clone());
    setOrgTask(savedTask.clone());
    openFollowUpFlow(savedTask);
  };

  const onFollowUpSave = async (template: Task) => {
    // Hide the follow-up dialog to avoid nested modals.
    setShowFollowUpModal(false);
    await createFromTemplate(template);
  };

  const onFollowUpSkip = async (notifyUserId?: string) => {
    setShowFollowUpModal(false);

    const finishedTask = task.clone();
    finishedTask.hasFollowUp = true;
    finishedTask.hasUnlinkedFollowUp = true;
    const newRelation = new TaskRelation(task.taskId > 0 ? task.taskId : 0, null, TaskRelationship.FollowUp);
    newRelation.notifyUserId = notifyUserId;
    finishedTask.taskRelations = [...finishedTask.taskRelations, newRelation];

    await onSave(finishedTask, false, false, { includeTaskRelations: true });
  };

  const getFollowUpDialog = (): JSX.Element => {
    return (
      <Fragment>
        <TaskFollowUpDialog
          isOpen={showFollowUpModal}
          onClose={() => setShowFollowUpModal(false)}
          onSave={onFollowUpSave}
          onSkip={onFollowUpSkip}
        />
        <DialogYesNo
          hidden={!showFollowUpSaveWarningDialog}
          onYes={() => {
            void onSaveBeforeFollowUp();
          }}
          onNo={() => {
            setShowFollowUpSaveWarningDialog(false);
          }}
          title={t('task:TopTasks.FollowUp')}
          subText={t('task:TopTasks.FollowUpSaveWarning')}
          yesText={t('translation:General.Button.Save')}
          noText={t('translation:General.Button.Cancel')}
        />
      </Fragment>
    );
  };

  const onLinkFollowUpToTemplate = async (relation: TaskRelation, template: Task) => {
    setPendingFollowUpLinkRelation(relation);
    logger.debug('FollowUpLink: create linked task from template requested', {
      parentTaskId: task.taskId,
      relation: toTaskRelationTrace(relation),
      templateTaskId: template.taskId,
      templateName: template.name,
    });
    await createFromTemplate(template);
  };

  const onCreateEmptyFollowUpTask = async (relation: TaskRelation) => {
    setPendingFollowUpLinkRelation(relation);
    logger.debug('FollowUpLink: create linked empty task requested', {
      parentTaskId: task.taskId,
      relation: toTaskRelationTrace(relation),
    });
    await createEmptyFollowUpTask();
  };

  const getShowFormErrorDialog = (): JSX.Element => {
    return (
      <DialogOk
        hidden={!showFormErrorDialog}
        onOk={() => {
          setShowFormErrorDialog(false);
        }}
        title={t('task:ShowFormErrorsDialog.Title')}
        subText={t('task:ShowFormErrorsDialog.SubText', {
          name: task.formVersion?.name ?? appContext.globalDataCache.taskTypes.get(task.taskTypeId).name,
          count: formErrors.length,
        })}
      />
    );
  };

  const getConvertChecklistDialog = (): JSX.Element | null => {
    if (!showConvertChecklistDialog) return null;

    return (
      <DialogYesNo
        hidden={!showConvertChecklistDialog}
        onYes={() => {
          onConvertChecklist();
        }}
        onNo={() => {
          setShowConvertChecklistDialog(false);
        }}
        title={t('task:ConvertChecklistDialog.Title')}
        subText={t('task:ConvertChecklistDialog.SubText')}
        optionalJSXGrow={true}
        optionalJSX={
          <IPStack tokens={globalStackTokensGapSmall}>
            {t('task:ConvertChecklistDialog.Benefits')}
            <ul>
              <li>{t('task:ConvertChecklistDialog.Item1')}</li>
              <li>{t('task:ConvertChecklistDialog.Item2')}</li>
              <li>{t('task:ConvertChecklistDialog.Item3')}</li>
            </ul>
            <IPStack.Item>
              <LearnMore learnMoreLink={globalKB_howToWorkWithForms} />
            </IPStack.Item>
            <IPStack.Item>
              <IPText>{t('task:ConvertChecklistDialog.Proceed')}</IPText>
            </IPStack.Item>
          </IPStack>
        }
      />
    );
  };

  const getConvertTaskTypeDialog = (): JSX.Element | null => {
    if (convertTaskTypeTarget === undefined) return null;

    if (convertTaskTypeTarget === TaskTypes.Monitoring) {
      return (
        <ConvertToAnnualPlanModal
          startDate={task.startDateTime}
          onClose={() => setConvertTaskTypeTarget(undefined)}
          onConvert={(pattern) => {
            onConvertTaskType(TaskTypes.Monitoring, pattern);
          }}
        />
      );
    }

    const toEvent = convertTaskTypeTarget === TaskTypes.Event;

    return (
      <DialogYesNo
        hidden={false}
        onYes={() => {
          onConvertTaskType(convertTaskTypeTarget);
        }}
        onNo={() => {
          setConvertTaskTypeTarget(undefined);
        }}
        title={toEvent ? t('task:ConvertTaskTypeDialog.ToEvent.Title') : t('task:ConvertTaskTypeDialog.ToTask.Title')}
        subText={
          toEvent ? t('task:ConvertTaskTypeDialog.ToEvent.SubText') : t('task:ConvertTaskTypeDialog.ToTask.SubText')
        }
        optionalJSXGrow={true}
        optionalJSX={<IPText>{t('task:ConvertTaskTypeDialog.Proceed')}</IPText>}
      />
    );
  };

  const onSetBeforeSaveCallback = (callback: BeforeSaveCallback) => {
    //to store a function in state, wrap it in an argumentless function
    setBeforeSaveCallback(() => callback);
  };

  //
  // Error handling
  //

  const getAppError = (error: string | unknown): AppError | undefined => {
    const appError = normalizeError(error);

    //when this instance is the same as the instance in the state, return the state
    if (error instanceof AppError && appError?.isEqual(error)) {
      return error;
    } else {
      return appError;
    }
  };

  const onError = (error: string | unknown) => {
    setError(getAppError(error));
  };

  const onAfterErrorAction = async (actionType: ICustomErrorActionType): Promise<void> => {
    switch (actionType) {
      case ICustomErrorActionType.Refresh:
        await reload();
        setError(undefined);
        break;
      case ICustomErrorActionType.Dismiss:
        setError(undefined);
        break;
      default:
        break;
    }
  };

  //
  // Evidence pane: the AI evidence assistant beside a saved evidence task opened from its criterion.
  // The agent reads and writes the STORED task, so a dirty modal is saved before every message and the
  // task is refreshed after every turn. Not on mobile: a third column does not fit.
  //
  // The refresh replaces the task in place. Not initData: that flips isInitializing, which renders the
  // loader INSTEAD of the modal and unmounts every pane - the assistant's conversation included.
  //The task moved to a newer form version while it was open here (an upgrade from another session,
  //the admin's "update open tasks") and the save carried answers for the old version, which the
  //server refused. Recovery: read the task back on its new version, keep every edit of this modal,
  //and carry the answers the user recorded over to the new version, question by question. A question
  //the new version no longer asks cannot take its answer; the user is told how many were left behind.
  //The form reloads on the new version with the carried answers in place, and the user saves again.
  const recoverFromFormVersionMove = async (edited: Task) => {
    //a row the form seeded and the user did not touch is not an answer to carry over
    const recorded = (edited.kpiData ?? []).filter((row) => row.kpi !== undefined && row.isRecordedAnswer());
    //the refused save changed nothing on the server, so the tags and links of this modal stand; the
    //read task becomes the saved state and the merged task the one the form loads from, in that order
    const fresh = await readTaskFromServer(false);
    if (!fresh || !fresh.taskTypeId) return;

    //the task read carries its version without the fields, and the form cache of this session still
    //holds the head it was filled with, so the new version's fields are read from the server
    let fields: TaskTypeFormElement[];
    try {
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      fields = await apiGetTaskTypeForm(fresh.taskTypeId, accessToken);
    } catch (err) {
      onError(err);

      return;
    }

    const fieldByKpiId = new Map(
      fields.filter((field) => field.kpiId !== undefined).map((field) => [field.kpiId, field]),
    );
    const carried: KPIData[] = [];
    let dropped = 0;
    for (const row of recorded) {
      const field = fieldByKpiId.get(row.kpiId);
      if (!field) {
        dropped++;
        continue;
      }

      const answer = row.cloneAsNewAnswer();
      //the attachments stay with the answer; they are links the save already created
      answer.contexts = row.contexts.filter((c) => c.entityType === EntityTypes.KPILink).map((c) => c.clone());
      //the comment and the attachments carry always; the value only when the new definition still
      //takes it: same type, a choice it still offers, a number inside its bounds
      if (field.kpi && !valueFitsDefinition(row, field.kpi)) {
        answer.resultNumber = undefined;
        answer.resultText = undefined;
        answer.resultDate = undefined;
        if (!answer.resultComment && answer.contexts.length === 0) {
          dropped++;
          continue;
        }
      }
      carried.push(answer);
    }

    //the version with its fields names the form tab and is the head the newer-version check compares
    //with, so the cache takes it over the head it was filled with - unless the family forked again
    //since the move and the cache already holds a later head, which must not be replaced by this one
    const version = fresh.formVersion?.clone();
    if (version) {
      version.form = fields;
      const cachedHead = appContext.globalDataCache.taskTypes.getHead(version.taskTypeId, version.taskTypeFamilyId);
      if (!cachedHead || (cachedHead.version ?? 0) <= (version.version ?? 0)) {
        appContext.globalDataCache.taskTypes.update(version);
      }
    }

    const recovered = edited.clone();
    recovered.taskTypeId = fresh.taskTypeId;
    recovered.taskTypeFamilyId = fresh.taskTypeFamilyId;
    recovered.formVersion = version;
    recovered.kpiData = carried;
    setOrgTask(fresh.clone());
    setTask(recovered);

    const versionNumber = fresh.formVersion?.version ?? '';
    appContext.showNotification(
      dropped > 0
        ? t('forms:Versioning.Conflicts.TaskFormVersionMovedDropped', { version: versionNumber, count: dropped })
        : t('forms:Versioning.Conflicts.TaskFormVersionMoved', { version: versionNumber }),
      false,
      true,
      true,
    );
  };

  //Whether the value a row holds is one the field's new definition takes. The row's definition
  //snapshot is the one it was answered under; a row never saved has none and is read against its kpi
  //recordedType: the type the value was recorded under when the row itself does not say (a copy's
  //rows travel as values only)
  const valueFitsDefinition = (row: KPIData, kpi: KPI, recordedType?: KPITypes): boolean => {
    const oldType = row.definition?.type ?? row.kpi?.type ?? recordedType;
    if (oldType !== undefined && oldType !== kpi.type) return false;

    if (kpi.type === KPITypes.Choice) {
      return row.resultNumber === undefined || (kpi.choices ?? []).some((c) => c.stringValueKey === row.resultNumber);
    }

    if (kpi.type === KPITypes.Number && row.resultNumber !== undefined) {
      const min = Number.parseFloat(kpi.minValue ?? '');
      const max = Number.parseFloat(kpi.maxValue ?? '');
      if (!isNaN(min) && row.resultNumber < min) return false;
      if (!isNaN(max) && row.resultNumber > max) return false;
    }

    return true;
  };

  //Reads the task back and, unless the caller applies it itself, shows it in the modal as the saved
  //state. The form loads its rows from the first task it sees on a new version, so a caller that
  //merges its own rows into the read task must hand the modal that merged task and nothing before it
  const readTaskFromServer = async (applyToModal: boolean = true): Promise<Task | undefined> => {
    try {
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const result = await apiGetSingleTask(task.taskId, false, accessToken, appContext.globalDataCache);
      if (!result || result.tasks.length !== 1) return undefined;

      const fresh = await loadTaskRelations(await loadSubTasks(result.tasks[0]));
      if (applyToModal) {
        setTaskTags(appContext.globalDataCache.tags.getItemsForId(fresh.tagIds));
        setTaskLinks(await apiGetLinksForTask(fresh.taskId, false, accessToken, appContext.globalDataCache));
        setOrgTask(fresh.clone());
        setTask(fresh.clone());
      }

      return fresh;
    } catch (err) {
      onError(err);

      return undefined;
    }
  };

  const refreshTaskFromServer = async (): Promise<void> => {
    await readTaskFromServer();
  };

  //the open instances of this series moved to the form's current version on the server. The modal
  //re-reads the master, the instance tab reloads, and the host receives the fresh master the way it
  //receives a saved one: the annual plan reloads the series' instances from it
  const onSeriesFormUpgraded = async (): Promise<void> => {
    const fresh = await readTaskFromServer();
    setInstancesReloadToken((token) => token + 1);
    if (fresh) await props.onSave(fresh, false, false);
  };

  //an inquiry's window wrote the task's state in its project room: the modal
  //re-reads the task and the host receives it the way it receives a saved one
  const onInquiryTaskChanged = async (): Promise<void> => {
    const fresh = await readTaskFromServer();
    if (!fresh) return;

    await props.onSave(fresh, false, false);
  };

  //a saved task, on a desktop, with the AI assistant licensed and switched on for the tenant
  const isAssistantAvailable = (): boolean =>
    task.taskId > 0 && !appContext.isMobileView && isCopilotPanelAvailable(appContext);

  //"Check evidence" (a preview feature): the assistant, a workable task (evidence lives on workable tasks only) and
  //the tenant's copilot.audit toggle; not on the criterion path, whose pane judges drafts itself, and not on an
  //assessment task, whose "Answer the assessment" reads the evidence per criterion (the check would judge it against
  //the checklist and description only)
  const isEvidenceCheckAvailable = (): boolean =>
    isAssistantAvailable() &&
    !props.evidenceContext &&
    task.isWorkableTask() &&
    task.taskKind !== TaskKinds.Assessment &&
    isPreviewFeatureToggleEnabled(appContext.globalDataCache.settings, ControlSceneAIActionsPreviewFeatureToggleCode);

  //"Audit the evidence with AI" (PLAN-CTI-090): the assistant on a stored assessment task that is still open, under the
  //Audit preview, which is when the AI button opens the pane on the assessment
  const isEvidenceAuditAvailable = (): boolean =>
    isAssistantAvailable() &&
    orgTask.taskKind === TaskKinds.Assessment &&
    orgTask.isWorkableTask() &&
    orgTask.isInWorkableState() &&
    isAuditCriteriaPreviewEnabled(appContext);

  const openAssistant = (action: TaskAssistantAction | undefined) => {
    setAssistantAction(action && isAssistantAvailable() ? action : undefined);
  };

  //the context action's proposal goes into the unsaved task as if the user had picked it in the Context tab:
  //the ids and the related entities the tab lists; Save stores it, Discard drops it. Returns how many were new
  const applyContextProposal = (items: CopilotContextProposalItem[]): number => {
    const newTask = task.clone();
    const relatedEntities = (newTask.relatedEntities ?? []).map((entity) => entity.clone());
    const linkedCount = () =>
      newTask.controlIds.length +
      newTask.processIds.length +
      newTask.objectiveIds.length +
      newTask.assetIds.length +
      newTask.riskIds.length;
    const before = linkedCount();

    for (const item of items) {
      const entity = new Entity(item.entityId, item.entityType, item.name, item.code);
      newTask.addEntity(entity);

      //an entry the user removed in the tab before comes back
      const related = relatedEntities.find((e) => e.typeOfEntity === item.entityType && e.entityId === item.entityId);
      if (related) {
        related.isDeleted = false;
      } else {
        relatedEntities.push(entity);
      }
    }

    const added = linkedCount() - before;
    if (added > 0) {
      newTask.relatedEntities = relatedEntities;
      setTask(newTask);
    }

    return added;
  };

  const getAssistantPane = (): JSX.Element | undefined => {
    if (!assistantAction || !isAssistantAvailable()) {
      return undefined;
    }

    const isTaskEmpty =
      !htmlToText(task.description).trim() &&
      task.checkList.items.length === 0 &&
      (task.resourceLinkIds ?? []).length === 0;

    return (
      <TaskAssistantPane
        task={task}
        action={assistantAction}
        //the proposal lands in the Context tab, so the context action needs the right to edit it
        canSuggestContext={canUpdateTaskField(orgTask, canUpdate, TaskFieldTypes.Context, appContext)}
        canCheckEvidence={isEvidenceCheckAvailable()}
        canAuditEvidence={isEvidenceAuditAvailable()}
        checkRequest={evidenceCheckRequest}
        isTaskEmpty={isTaskEmpty}
        beforeSend={async () => (orgTask.isEqual(task) ? true : await saveWithoutClose())}
        onTurnCompleted={refreshTaskFromServer}
        onContextProposal={applyContextProposal}
        //the criterion path keeps the pane for the life of the modal; a pane the user opened closes again
        onClose={props.evidenceContext ? undefined : () => setAssistantAction(undefined)}
      />
    );
  };

  //
  // Main render
  //
  const showTaskTypeLoader = isLoading && !followUpTask && !taskCopy;

  if (isInitializing) {
    return <OverlayLoader text={t('translation:General.Notifications.Loading')} />;
  }

  switch (task.taskType) {
    case TaskTypes.Normal:
      // a Kickstart asset inquiry is an ordinary task whose window is the responsible person's list:
      // they answer it there and complete it with the inquiry's own button
      // a Kickstart outcome inquiry likewise, with the role holder's outcomes per document
      if (task.taskKind === TaskKinds.AssetInquiry || task.taskKind === TaskKinds.OutcomeInquiry) {
        const InquiryTask = task.taskKind === TaskKinds.OutcomeInquiry ? OutcomeInquiryTask : AssetInquiryTask;

        return (
          <Suspense fallback={<OverlayLoader text={t('translation:General.Notifications.Loading')} />}>
            <InquiryTask
              task={task}
              orgTask={orgTask}
              isOpen={props.isOpen}
              onSave={onSave}
              onCancel={onCancel}
              onUpdateTask={onUpdate}
              isActionPending={isActionPending}
              isLoading={showTaskTypeLoader}
              windowLevel={props.windowLevel}
              error={error}
              onAfterErrorAction={onAfterErrorAction}
              onError={onError}
              inquiryOpensAt={props.inquiryOpensAt}
              onTaskChanged={onInquiryTaskChanged}
            ></InquiryTask>
            {getAdditionalTaskModal()}
          </Suspense>
        );
      }

      // an audit execution task is an ordinary task with a guided pane: the auditee works on the
      // item the audit put in front of them and records the result there
      if (task.taskKind === TaskKinds.InternalAudit) {
        return (
          <Fragment>
            <AuditExecutionTask
              onChangeCompletionDate={updateCompletionDate}
              onReopenTask={reopenTask}
              task={task}
              orgTask={orgTask}
              isOpen={props.isOpen}
              onSave={onSave}
              saveWithoutClose={saveWithoutClose}
              onRemove={onRemove}
              onCancel={onCancel}
              onCreateNewTaskTemplate={onCreateNewTaskTemplate}
              onUpdateTask={onUpdate}
              onUpdateOrgTask={setOrgTask}
              copyFromTemplate={copyFromTemplate}
              createFromTemplate={createFromTemplate}
              onClickFollowUp={onClickFollowUp}
              onLinkFollowUpToTemplate={onLinkFollowUpToTemplate}
              onCreateEmptyFollowUpTask={onCreateEmptyFollowUpTask}
              isActionPending={isActionPending}
              isLoading={showTaskTypeLoader}
              setIsActionPending={setIsActionPending}
              createInOutlook={createInOutlook}
              removeFromOutlook={removeFromOutlook}
              loadRescheduleRange={loadRescheduleRange}
              rescheduleDateRange={rescheduleDateRange}
              tags={taskTags}
              addTagToTaskState={addTagToTaskState}
              removeTagFromTaskState={removeTagFromTaskState}
              links={taskLinks}
              addLinkToTaskState={addLinkToTaskState}
              setLinkToTaskState={setLinkToTaskState}
              removeLinkFromTaskState={removeLinkFromTaskState}
              navigateToEventURL={navigateToEventURL}
              disallowSchedule={getDisallowSchedule()}
              disallowSave={props.disallowSave}
              onUpdateKPIData={onUpdateKPIData}
              onSetKPIData={onSetKPIData}
              onUpdateTaskForForm={onUpdateTaskForForm}
              windowLevel={props.windowLevel}
              showFormErrors={showFormErrors}
              onValidateForm={onValidateForm}
              selectedPivot={selectedPivot}
              onBeforeSaveCallback={onSetBeforeSaveCallback}
              hasFormFeature={hasFormFeature}
              initData={initData}
              reload={reload}
              navigateExternal={props.navigateExternal}
              hasPAFeature={hasPAFeature}
              canUpdate={canUpdate}
              canDelete={canDelete}
              compactView={compactView}
              initPivot={initPivot}
              setCompactView={setCompactView}
              formMode={formMode}
              setFormMode={setFormMode}
              setSelectedPivot={setSelectedPivot}
              error={error}
              onAfterErrorAction={onAfterErrorAction}
              onError={onError}
              onUploadEvidence={onUploadEvidence}
            ></AuditExecutionTask>
            {getAdditionalTaskModal()}
            {getFollowUpDialog()}
            {getShowFormErrorDialog()}
            {getUploadEvidenceDialog()}
          </Fragment>
        );
      }

      if (
        props.useCreateWizard &&
        task.taskId === -1 &&
        task.systemTaskType === SystemTaskTypes.None &&
        !appContext.isMobileView &&
        !forceFullDialog
      ) {
        return (
          <Fragment>
            <NewTaskWizardWithAI
              isOpen={props.isOpen}
              task={task}
              orgTask={orgTask}
              canUpdate={canUpdate}
              isActionPending={isActionPending}
              isLoading={showTaskTypeLoader}
              windowLevel={props.windowLevel}
              onUpdateTask={onUpdate}
              onUpdateOrgTask={setOrgTask}
              onSave={onSave}
              onCancel={onCancel}
              onOpenFullDialog={() => setForceFullDialog(true)}
            />
            {getAdditionalTaskModal()}
            {getFollowUpDialog()}
            {getShowFormErrorDialog()}
            {getUploadEvidenceDialog()}
          </Fragment>
        );
      }

      return (
        <Fragment>
          <NormalTask
            taskDetailView={taskDetailView}
            sidePane={getAssistantPane()}
            onSeriesFormUpgraded={onSeriesFormUpgraded}
            instancesReloadToken={instancesReloadToken}
            assistantAction={assistantAction}
            onOpenAssistant={openAssistant}
            onSaveAsNewTask={onSaveAsNewTask}
            onConvertTaskType={(targetType) => setConvertTaskTypeTarget(targetType)}
            onChangeCompletionDate={updateCompletionDate}
            task={task}
            orgTask={orgTask}
            isOpen={props.isOpen}
            onSave={onSave}
            onRemove={onRemove}
            onCancel={onCancel}
            onCreateNewTaskTemplate={onCreateNewTaskTemplate}
            onUpdateTask={onUpdate}
            onUpdateOrgTask={setOrgTask}
            copyFromTemplate={copyFromTemplate}
            createFromTemplate={createFromTemplate}
            onClickFollowUp={onClickFollowUp}
            onLinkFollowUpToTemplate={onLinkFollowUpToTemplate}
            onCreateEmptyFollowUpTask={onCreateEmptyFollowUpTask}
            isActionPending={isActionPending}
            isLoading={showTaskTypeLoader}
            setIsActionPending={setIsActionPending}
            createInOutlook={createInOutlook}
            removeFromOutlook={removeFromOutlook}
            loadRescheduleRange={loadRescheduleRange}
            rescheduleDateRange={rescheduleDateRange}
            tags={taskTags}
            addTagToTaskState={addTagToTaskState}
            removeTagFromTaskState={removeTagFromTaskState}
            links={taskLinks}
            addLinkToTaskState={addLinkToTaskState}
            setLinkToTaskState={setLinkToTaskState}
            removeLinkFromTaskState={removeLinkFromTaskState}
            navigateToEventURL={navigateToEventURL}
            disallowSchedule={getDisallowSchedule()}
            disallowSave={props.disallowSave}
            onUpdateKPIData={onUpdateKPIData}
            onSetKPIData={onSetKPIData}
            onUpdateTaskForForm={onUpdateTaskForForm}
            windowLevel={props.windowLevel}
            showFormErrors={showFormErrors}
            onValidateForm={onValidateForm}
            selectedPivot={selectedPivot}
            onBeforeSaveCallback={onSetBeforeSaveCallback}
            hasFormFeature={hasFormFeature}
            initData={initData}
            reload={reload}
            navigateExternal={props.navigateExternal}
            hasPAFeature={hasPAFeature}
            canUpdate={canUpdate}
            canDelete={canDelete}
            compactView={compactView}
            initPivot={initPivot}
            setCompactView={setCompactView}
            formMode={formMode}
            setFormMode={setFormMode}
            setSelectedPivot={setSelectedPivot}
            error={error}
            onAfterErrorAction={onAfterErrorAction}
            onError={onError}
            onUploadEvidence={onUploadEvidence}
            pendingComments={pendingComments}
            onQueueComment={onQueueComment}
            onRemovePendingComment={onRemovePendingComment}
          ></NormalTask>
          {getAdditionalTaskModal()}
          {getFollowUpDialog()}
          {getShowFormErrorDialog()}
          {getUploadEvidenceDialog()}
          {getConvertTaskTypeDialog()}
        </Fragment>
      );
    case TaskTypes.Event:
      switch (task.taskKind) {
        case TaskKinds.Approval:
          return (
            <Fragment>
              <ApprovalTask
                onSaveAsNewTask={onSaveAsNewTask}
                onChangeCompletionDate={updateCompletionDate}
                task={task}
                orgTask={orgTask}
                isOpen={props.isOpen}
                onSave={onSave}
                onRemove={onRemove}
                onCancel={onCancel}
                onCreateNewTaskTemplate={onCreateNewTaskTemplate}
                onUpdateTask={onUpdate}
                onUpdateOrgTask={setOrgTask}
                copyFromTemplate={copyFromTemplate}
                createFromTemplate={createFromTemplate}
                onClickFollowUp={onClickFollowUp}
                onLinkFollowUpToTemplate={onLinkFollowUpToTemplate}
                onCreateEmptyFollowUpTask={onCreateEmptyFollowUpTask}
                isActionPending={isActionPending}
                isLoading={showTaskTypeLoader}
                setIsActionPending={setIsActionPending}
                createInOutlook={createInOutlook}
                removeFromOutlook={removeFromOutlook}
                loadRescheduleRange={loadRescheduleRange}
                rescheduleDateRange={rescheduleDateRange}
                tags={taskTags}
                addTagToTaskState={addTagToTaskState}
                removeTagFromTaskState={removeTagFromTaskState}
                links={taskLinks}
                addLinkToTaskState={addLinkToTaskState}
                setLinkToTaskState={setLinkToTaskState}
                removeLinkFromTaskState={removeLinkFromTaskState}
                navigateToEventURL={navigateToEventURL}
                disallowSchedule={getDisallowSchedule()}
                disallowSave={props.disallowSave}
                onUpdateKPIData={onUpdateKPIData}
                onSetKPIData={onSetKPIData}
                onUpdateTaskForForm={onUpdateTaskForForm}
                windowLevel={props.windowLevel}
                showFormErrors={showFormErrors}
                onValidateForm={onValidateForm}
                selectedPivot={selectedPivot}
                onBeforeSaveCallback={onSetBeforeSaveCallback}
                hasFormFeature={hasFormFeature}
                initData={initData}
                reload={reload}
                navigateExternal={props.navigateExternal}
                hasPAFeature={hasPAFeature}
                canUpdate={canUpdate}
                canDelete={canDelete}
                compactView={compactView}
                initPivot={initPivot}
                setCompactView={setCompactView}
                formMode={formMode}
                setFormMode={setFormMode}
                setSelectedPivot={setSelectedPivot}
                error={error}
                onAfterErrorAction={onAfterErrorAction}
                onError={onError}
                onUploadEvidence={onUploadEvidence}
                masterTask={masterTask}
              />
              {getAdditionalTaskModal()}
              {getFollowUpDialog()}
              {getShowFormErrorDialog()}
            </Fragment>
          );
        case TaskKinds.ProcessingActivityApproval:
          return (
            <Fragment>
              <ProcessingActivityApprovalTask
                onSaveAsNewTask={onSaveAsNewTask}
                onChangeCompletionDate={updateCompletionDate}
                task={task}
                orgTask={orgTask}
                isOpen={props.isOpen}
                onSave={onSave}
                onRemove={onRemove}
                onCancel={onCancel}
                onCreateNewTaskTemplate={onCreateNewTaskTemplate}
                onUpdateTask={onUpdate}
                onUpdateOrgTask={setOrgTask}
                copyFromTemplate={copyFromTemplate}
                createFromTemplate={createFromTemplate}
                onClickFollowUp={onClickFollowUp}
                onLinkFollowUpToTemplate={onLinkFollowUpToTemplate}
                isActionPending={isActionPending}
                isLoading={showTaskTypeLoader}
                setIsActionPending={setIsActionPending}
                createInOutlook={createInOutlook}
                removeFromOutlook={removeFromOutlook}
                loadRescheduleRange={loadRescheduleRange}
                rescheduleDateRange={rescheduleDateRange}
                tags={taskTags}
                addTagToTaskState={addTagToTaskState}
                removeTagFromTaskState={removeTagFromTaskState}
                links={taskLinks}
                addLinkToTaskState={addLinkToTaskState}
                setLinkToTaskState={setLinkToTaskState}
                removeLinkFromTaskState={removeLinkFromTaskState}
                navigateToEventURL={navigateToEventURL}
                disallowSchedule={getDisallowSchedule()}
                disallowSave={props.disallowSave}
                onUpdateKPIData={onUpdateKPIData}
                onSetKPIData={onSetKPIData}
                onUpdateTaskForForm={onUpdateTaskForForm}
                windowLevel={props.windowLevel}
                showFormErrors={showFormErrors}
                onValidateForm={onValidateForm}
                selectedPivot={selectedPivot}
                onBeforeSaveCallback={onSetBeforeSaveCallback}
                hasFormFeature={hasFormFeature}
                initData={initData}
                reload={reload}
                navigateExternal={props.navigateExternal}
                hasPAFeature={hasPAFeature}
                canUpdate={canUpdate}
                canDelete={canDelete}
                compactView={compactView}
                initPivot={initPivot}
                setCompactView={setCompactView}
                formMode={formMode}
                setFormMode={setFormMode}
                setSelectedPivot={setSelectedPivot}
                error={error}
                onAfterErrorAction={onAfterErrorAction}
                onError={onError}
                onUploadEvidence={onUploadEvidence}
                masterTask={masterTask}
              />
              {getAdditionalTaskModal()}
              {getFollowUpDialog()}
            </Fragment>
          );
        default:
          return (
            <Fragment>
              <EventTask
                sidePane={getAssistantPane()}
                assistantAction={assistantAction}
                onOpenAssistant={openAssistant}
                onSaveAsNewTask={onSaveAsNewTask}
                onConvertTaskType={(targetType) => setConvertTaskTypeTarget(targetType)}
                onChangeCompletionDate={updateCompletionDate}
                task={task}
                orgTask={orgTask}
                isOpen={props.isOpen}
                onSave={onSave}
                onRemove={onRemove}
                onCancel={onCancel}
                onCreateNewTaskTemplate={onCreateNewTaskTemplate}
                onUpdateTask={onUpdate}
                onUpdateOrgTask={setOrgTask}
                copyFromTemplate={copyFromTemplate}
                createFromTemplate={createFromTemplate}
                onClickFollowUp={onClickFollowUp}
                onLinkFollowUpToTemplate={onLinkFollowUpToTemplate}
                onCreateEmptyFollowUpTask={onCreateEmptyFollowUpTask}
                isActionPending={isActionPending}
                isLoading={showTaskTypeLoader}
                setIsActionPending={setIsActionPending}
                createInOutlook={createInOutlook}
                removeFromOutlook={removeFromOutlook}
                loadRescheduleRange={loadRescheduleRange}
                rescheduleDateRange={rescheduleDateRange}
                tags={taskTags}
                addTagToTaskState={addTagToTaskState}
                removeTagFromTaskState={removeTagFromTaskState}
                links={taskLinks}
                addLinkToTaskState={addLinkToTaskState}
                setLinkToTaskState={setLinkToTaskState}
                removeLinkFromTaskState={removeLinkFromTaskState}
                navigateToEventURL={navigateToEventURL}
                disallowSchedule={getDisallowSchedule()}
                disallowSave={props.disallowSave}
                onUpdateKPIData={onUpdateKPIData}
                onSetKPIData={onSetKPIData}
                onUpdateTaskForForm={onUpdateTaskForForm}
                windowLevel={props.windowLevel}
                showFormErrors={showFormErrors}
                onValidateForm={onValidateForm}
                selectedPivot={selectedPivot}
                onBeforeSaveCallback={onSetBeforeSaveCallback}
                hasFormFeature={hasFormFeature}
                initData={initData}
                reload={reload}
                navigateExternal={props.navigateExternal}
                hasPAFeature={hasPAFeature}
                canUpdate={canUpdate}
                canDelete={canDelete}
                compactView={compactView}
                initPivot={initPivot}
                setCompactView={setCompactView}
                formMode={formMode}
                setFormMode={setFormMode}
                setSelectedPivot={setSelectedPivot}
                error={error}
                onAfterErrorAction={onAfterErrorAction}
                onError={onError}
                onUploadEvidence={onUploadEvidence}
                pendingComments={pendingComments}
                onQueueComment={onQueueComment}
                onRemovePendingComment={onRemovePendingComment}
              ></EventTask>
              {getAdditionalTaskModal()}
              {getFollowUpDialog()}
              {getShowFormErrorDialog()}
              {getUploadEvidenceDialog()}
              {getConvertTaskTypeDialog()}
            </Fragment>
          );
      }
    case TaskTypes.EventTemplate:
      switch (task.taskKind) {
        case TaskKinds.Approval:
          return (
            <Fragment>
              <ApprovalTemplateTask
                onSaveAsNewTask={onSaveAsNewTask}
                onChangeCompletionDate={updateCompletionDate}
                task={task}
                orgTask={orgTask}
                isOpen={props.isOpen}
                onSave={onSave}
                onRemove={onRemove}
                onCancel={onCancel}
                onCreateNewTaskTemplate={onCreateNewTaskTemplate}
                onUpdateTask={onUpdate}
                onUpdateOrgTask={setOrgTask}
                copyFromTemplate={copyFromTemplate}
                createFromTemplate={createFromTemplate}
                onClickFollowUp={onClickFollowUp}
                onLinkFollowUpToTemplate={onLinkFollowUpToTemplate}
                isActionPending={isActionPending}
                isLoading={showTaskTypeLoader}
                setIsActionPending={setIsActionPending}
                createInOutlook={createInOutlook}
                removeFromOutlook={removeFromOutlook}
                loadRescheduleRange={loadRescheduleRange}
                rescheduleDateRange={rescheduleDateRange}
                tags={taskTags}
                addTagToTaskState={addTagToTaskState}
                removeTagFromTaskState={removeTagFromTaskState}
                links={taskLinks}
                addLinkToTaskState={addLinkToTaskState}
                setLinkToTaskState={setLinkToTaskState}
                removeLinkFromTaskState={removeLinkFromTaskState}
                navigateToEventURL={navigateToEventURL}
                disallowSchedule={getDisallowSchedule()}
                disallowSave={props.disallowSave}
                onUpdateKPIData={onUpdateKPIData}
                onSetKPIData={onSetKPIData}
                onUpdateTaskForForm={onUpdateTaskForForm}
                windowLevel={props.windowLevel}
                showFormErrors={showFormErrors}
                onValidateForm={onValidateForm}
                selectedPivot={selectedPivot}
                onBeforeSaveCallback={onSetBeforeSaveCallback}
                hasFormFeature={hasFormFeature}
                initData={initData}
                reload={reload}
                navigateExternal={props.navigateExternal}
                hasPAFeature={hasPAFeature}
                canUpdate={canUpdate}
                canDelete={canDelete}
                compactView={compactView}
                initPivot={initPivot}
                setCompactView={setCompactView}
                formMode={formMode}
                setFormMode={setFormMode}
                setSelectedPivot={setSelectedPivot}
                error={error}
                onAfterErrorAction={onAfterErrorAction}
                onError={onError}
                onUploadEvidence={onUploadEvidence}
              />
              {getAdditionalTaskModal()}
              {getFollowUpDialog()}
              {getShowFormErrorDialog()}
            </Fragment>
          );
        case TaskKinds.InternalAudit:
          return (
            <Fragment>
              <AuditTemplateTask
                onSaveAsNewTask={onSaveAsNewTask}
                onChangeCompletionDate={updateCompletionDate}
                task={task}
                orgTask={orgTask}
                isOpen={props.isOpen}
                onSave={onSave}
                onRemove={onRemove}
                onCancel={onCancel}
                onCreateNewTaskTemplate={onCreateNewTaskTemplate}
                onUpdateTask={onUpdate}
                onUpdateOrgTask={setOrgTask}
                copyFromTemplate={copyFromTemplate}
                createFromTemplate={createFromTemplate}
                onClickFollowUp={onClickFollowUp}
                onLinkFollowUpToTemplate={onLinkFollowUpToTemplate}
                isActionPending={isActionPending}
                isLoading={showTaskTypeLoader}
                setIsActionPending={setIsActionPending}
                createInOutlook={createInOutlook}
                removeFromOutlook={removeFromOutlook}
                loadRescheduleRange={loadRescheduleRange}
                rescheduleDateRange={rescheduleDateRange}
                tags={taskTags}
                addTagToTaskState={addTagToTaskState}
                removeTagFromTaskState={removeTagFromTaskState}
                links={taskLinks}
                addLinkToTaskState={addLinkToTaskState}
                setLinkToTaskState={setLinkToTaskState}
                removeLinkFromTaskState={removeLinkFromTaskState}
                navigateToEventURL={navigateToEventURL}
                disallowSchedule={getDisallowSchedule()}
                disallowSave={props.disallowSave}
                onUpdateKPIData={onUpdateKPIData}
                onSetKPIData={onSetKPIData}
                onUpdateTaskForForm={onUpdateTaskForForm}
                windowLevel={props.windowLevel}
                showFormErrors={showFormErrors}
                onValidateForm={onValidateForm}
                selectedPivot={selectedPivot}
                onBeforeSaveCallback={onSetBeforeSaveCallback}
                hasFormFeature={hasFormFeature}
                initData={initData}
                reload={reload}
                navigateExternal={props.navigateExternal}
                hasPAFeature={hasPAFeature}
                canUpdate={canUpdate}
                canDelete={canDelete}
                compactView={compactView}
                initPivot={initPivot}
                setCompactView={setCompactView}
                formMode={formMode}
                setFormMode={setFormMode}
                setSelectedPivot={setSelectedPivot}
                onConvertChecklist={() => setShowConvertChecklistDialog(true)}
                error={error}
                onAfterErrorAction={onAfterErrorAction}
                onError={onError}
                onUploadEvidence={onUploadEvidence}
                siblingNames={props.siblingNames}
              ></AuditTemplateTask>
              {getAdditionalTaskModal()}
              {getFollowUpDialog()}
              {getShowFormErrorDialog()}
              {getConvertChecklistDialog()}
            </Fragment>
          );
        default:
          return (
            <Fragment>
              <EventTemplateTask
                onSaveAsNewTask={onSaveAsNewTask}
                onChangeCompletionDate={updateCompletionDate}
                task={task}
                orgTask={orgTask}
                isOpen={props.isOpen}
                onSave={onSave}
                onRemove={onRemove}
                onCancel={onCancel}
                onCreateNewTaskTemplate={onCreateNewTaskTemplate}
                onUpdateTask={onUpdate}
                onUpdateOrgTask={setOrgTask}
                copyFromTemplate={copyFromTemplate}
                createFromTemplate={createFromTemplate}
                onClickFollowUp={onClickFollowUp}
                onLinkFollowUpToTemplate={onLinkFollowUpToTemplate}
                isActionPending={isActionPending}
                isLoading={showTaskTypeLoader}
                setIsActionPending={setIsActionPending}
                createInOutlook={createInOutlook}
                removeFromOutlook={removeFromOutlook}
                loadRescheduleRange={loadRescheduleRange}
                rescheduleDateRange={rescheduleDateRange}
                tags={taskTags}
                addTagToTaskState={addTagToTaskState}
                removeTagFromTaskState={removeTagFromTaskState}
                links={taskLinks}
                addLinkToTaskState={addLinkToTaskState}
                setLinkToTaskState={setLinkToTaskState}
                removeLinkFromTaskState={removeLinkFromTaskState}
                navigateToEventURL={navigateToEventURL}
                disallowSchedule={getDisallowSchedule()}
                disallowSave={props.disallowSave}
                onUpdateKPIData={onUpdateKPIData}
                onSetKPIData={onSetKPIData}
                onUpdateTaskForForm={onUpdateTaskForForm}
                windowLevel={props.windowLevel}
                showFormErrors={showFormErrors}
                onValidateForm={onValidateForm}
                selectedPivot={selectedPivot}
                onBeforeSaveCallback={onSetBeforeSaveCallback}
                hasFormFeature={hasFormFeature}
                initData={initData}
                reload={reload}
                navigateExternal={props.navigateExternal}
                hasPAFeature={hasPAFeature}
                canUpdate={canUpdate}
                canDelete={canDelete}
                compactView={compactView}
                initPivot={initPivot}
                setCompactView={setCompactView}
                formMode={formMode}
                setFormMode={setFormMode}
                setSelectedPivot={setSelectedPivot}
                onConvertChecklist={() => setShowConvertChecklistDialog(true)}
                error={error}
                onAfterErrorAction={onAfterErrorAction}
                onError={onError}
                onUploadEvidence={onUploadEvidence}
              ></EventTemplateTask>
              {getAdditionalTaskModal()}
              {getFollowUpDialog()}
              {getShowFormErrorDialog()}
              {getConvertChecklistDialog()}
            </Fragment>
          );
      }
    case TaskTypes.Template:
      return (
        <Fragment>
          <TemplateTask
            onSaveAsNewTask={onSaveAsNewTask}
            onChangeCompletionDate={updateCompletionDate}
            task={task}
            orgTask={orgTask}
            isOpen={props.isOpen}
            onSave={onSave}
            onRemove={onRemove}
            onCancel={onCancel}
            onCreateNewTaskTemplate={onCreateNewTaskTemplate}
            onUpdateTask={onUpdate}
            onUpdateOrgTask={setOrgTask}
            copyFromTemplate={copyFromTemplate}
            createFromTemplate={createFromTemplate}
            onClickFollowUp={onClickFollowUp}
            onLinkFollowUpToTemplate={onLinkFollowUpToTemplate}
            onCreateEmptyFollowUpTask={onCreateEmptyFollowUpTask}
            isActionPending={isActionPending}
            isLoading={showTaskTypeLoader}
            setIsActionPending={setIsActionPending}
            createInOutlook={createInOutlook}
            removeFromOutlook={removeFromOutlook}
            loadRescheduleRange={loadRescheduleRange}
            rescheduleDateRange={rescheduleDateRange}
            tags={taskTags}
            addTagToTaskState={addTagToTaskState}
            removeTagFromTaskState={removeTagFromTaskState}
            links={taskLinks}
            addLinkToTaskState={addLinkToTaskState}
            setLinkToTaskState={setLinkToTaskState}
            removeLinkFromTaskState={removeLinkFromTaskState}
            navigateToEventURL={navigateToEventURL}
            disallowSchedule={getDisallowSchedule()}
            disallowSave={props.disallowSave}
            onUpdateKPIData={onUpdateKPIData}
            onSetKPIData={onSetKPIData}
            onUpdateTaskForForm={onUpdateTaskForForm}
            windowLevel={props.windowLevel}
            showFormErrors={showFormErrors}
            onValidateForm={onValidateForm}
            selectedPivot={selectedPivot}
            onBeforeSaveCallback={onSetBeforeSaveCallback}
            hasFormFeature={hasFormFeature}
            initData={initData}
            reload={reload}
            navigateExternal={props.navigateExternal}
            hasPAFeature={hasPAFeature}
            canUpdate={canUpdate}
            canDelete={canDelete}
            compactView={compactView}
            initPivot={initPivot}
            setCompactView={setCompactView}
            formMode={formMode}
            setFormMode={setFormMode}
            setSelectedPivot={setSelectedPivot}
            onConvertChecklist={() => setShowConvertChecklistDialog(true)}
            error={error}
            onAfterErrorAction={onAfterErrorAction}
            onError={onError}
            onUploadEvidence={onUploadEvidence}
          ></TemplateTask>
          {getAdditionalTaskModal()}
          {getFollowUpDialog()}
          {getShowFormErrorDialog()}
          {getConvertChecklistDialog()}
        </Fragment>
      );
    case TaskTypes.Monitoring:
      return (
        <Fragment>
          <MonitoringTask
            sidePane={getAssistantPane()}
            onSeriesFormUpgraded={onSeriesFormUpgraded}
            instancesReloadToken={instancesReloadToken}
            assistantAction={assistantAction}
            onOpenAssistant={openAssistant}
            onSaveAsNewTask={onSaveAsNewTask}
            onChangeCompletionDate={updateCompletionDate}
            task={task}
            orgTask={orgTask}
            isOpen={props.isOpen}
            onSave={onSave}
            onRemove={onRemove}
            onCancel={onCancel}
            onCreateNewTaskTemplate={onCreateNewTaskTemplate}
            onUpdateTask={onUpdate}
            onUpdateOrgTask={setOrgTask}
            copyFromTemplate={copyFromTemplate}
            createFromTemplate={createFromTemplate}
            onClickFollowUp={onClickFollowUp}
            onLinkFollowUpToTemplate={onLinkFollowUpToTemplate}
            onCreateEmptyFollowUpTask={onCreateEmptyFollowUpTask}
            isActionPending={isActionPending}
            isLoading={showTaskTypeLoader}
            setIsActionPending={setIsActionPending}
            createInOutlook={createInOutlook}
            removeFromOutlook={removeFromOutlook}
            loadRescheduleRange={loadRescheduleRange}
            rescheduleDateRange={rescheduleDateRange}
            tags={taskTags}
            addTagToTaskState={addTagToTaskState}
            removeTagFromTaskState={removeTagFromTaskState}
            links={taskLinks}
            addLinkToTaskState={addLinkToTaskState}
            setLinkToTaskState={setLinkToTaskState}
            removeLinkFromTaskState={removeLinkFromTaskState}
            navigateToEventURL={navigateToEventURL}
            disallowSchedule={getDisallowSchedule()}
            disallowSave={props.disallowSave}
            onUpdateKPIData={onUpdateKPIData}
            onSetKPIData={onSetKPIData}
            onUpdateTaskForForm={onUpdateTaskForForm}
            windowLevel={props.windowLevel}
            showFormErrors={showFormErrors}
            onValidateForm={onValidateForm}
            selectedPivot={selectedPivot}
            onBeforeSaveCallback={onSetBeforeSaveCallback}
            hasFormFeature={hasFormFeature}
            initData={initData}
            reload={reload}
            navigateExternal={props.navigateExternal}
            hasPAFeature={hasPAFeature}
            canUpdate={canUpdate}
            canDelete={canDelete}
            compactView={compactView}
            initPivot={initPivot}
            setCompactView={setCompactView}
            formMode={formMode}
            setFormMode={setFormMode}
            setSelectedPivot={setSelectedPivot}
            onConvertChecklist={() => setShowConvertChecklistDialog(true)}
            error={error}
            onError={onError}
            onAfterErrorAction={onAfterErrorAction}
            onUploadEvidence={onUploadEvidence}
            masterTask={masterTask}
          ></MonitoringTask>
          {getAdditionalTaskModal()}
          {getFollowUpDialog()}
          {getShowFormErrorDialog()}
          {getConvertChecklistDialog()}
          {getUploadEvidenceDialog()}
        </Fragment>
      );
    default:
      throw new AppError('Unknown task type');
  }
};

export default SingleTask;
